import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Users, BarChart3, DollarSign, FileText,
  Sparkles, Presentation, Video, GraduationCap, Map, RefreshCw,
} from 'lucide-react';
import AdminDashboardShell from './AdminDashboardShell';
import './CreatorStudio.css';

/* ================================================================
   AI STUDIO TAB
   ================================================================ */
const AIStudioTab = ({ API_BASE, token }) => {
  const [content, setContent] = useState('');
  const [contentType, setContentType] = useState('lesson');
  const [topic, setTopic] = useState('');
  const [language, setLanguage] = useState('English');
  const [level, setLevel] = useState('intermediate');
  const [audience, setAudience] = useState('learners');
  const [loading, setLoading] = useState(false);
  const [improveMode, setImproveMode] = useState('improve');
  const [statusMsg, setStatusMsg] = useState('');

  const callAI = async (endpoint, body) => {
    setLoading(true); setStatusMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/creator/ai/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) return data.content;
      setStatusMsg('❌ ' + (data.message || 'Error'));
      return null;
    } catch (e) { setStatusMsg('❌ Network error'); return null; }
    finally { setLoading(false); }
  };

  const handleGenerate = async () => {
    if (!topic.trim()) return setStatusMsg('⚠️ Enter a topic first');
    const result = await callAI('generate', { type: contentType, topic, options: { level, audience, language } });
    if (result) { setContent(result); setStatusMsg('✅ Content generated!'); }
  };

  const handleImprove = async () => {
    if (!content.trim()) return setStatusMsg('⚠️ Write or generate content first');
    const result = await callAI('improve', { content, mode: improveMode, language });
    if (result) { setContent(result); setStatusMsg(`✅ Content ${improveMode}d!`); }
  };

  const handleTranslate = async () => {
    if (!content.trim()) return setStatusMsg('⚠️ Write or generate content first');
    const result = await callAI('translate', { content, targetLanguage: language });
    if (result) { setContent(result); setStatusMsg(`✅ Translated to ${language}!`); }
  };

  const copyContent = () => { navigator.clipboard.writeText(content); setStatusMsg('✅ Copied to clipboard!'); };
  const printContent = () => { const w = window.open(); w.document.write(`<pre style="font-family:sans-serif;padding:2rem;max-width:800px;margin:auto;line-height:1.8">${content}</pre>`); w.print(); w.close(); };

  const statusClass = statusMsg.startsWith('✅') ? 'success' : statusMsg.startsWith('⚠️') ? 'warning' : 'error';

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <div className="ai-tab-icon purple">🤖</div>
        <div>
          <h2 className="ai-tab-title purple">AI Studio</h2>
          <p className="ai-tab-subtitle">Generate, improve, and translate content with Claude &amp; Grok AI</p>
        </div>
      </div>

      <div className="ai-studio-grid">
        {/* Left Panel — Controls */}
        <div className="ai-studio-sidebar">
          {/* Generate Panel */}
          <div className="ai-glass-panel">
            <h3 className="purple">⚡ Generate Content</h3>
            <div className="ai-form-group">
              <label className="ai-label">Content Type</label>
              <select value={contentType} onChange={e => setContentType(e.target.value)} className="ai-select">
                {['lesson','article','quiz','script','outline','blog'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
              </select>
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Topic</label>
              <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Introduction to React Hooks" className="ai-input" />
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Level</label>
              <select value={level} onChange={e => setLevel(e.target.value)} className="ai-select">
                {['beginner','intermediate','advanced','expert'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Audience</label>
              <input value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. engineering students" className="ai-input" />
            </div>
            <button onClick={handleGenerate} disabled={loading} className="ai-btn purple">
              {loading ? '⏳ Generating...' : '✨ Generate'}
            </button>
          </div>

          {/* Improve Panel */}
          <div className="ai-glass-panel">
            <h3 className="green">🔧 Improve Content</h3>
            <div className="ai-form-group">
              <label className="ai-label">Mode</label>
              <select value={improveMode} onChange={e => setImproveMode(e.target.value)} className="ai-select">
                {['improve','expand','summarize','grammar','simplify','formal'].map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <button onClick={handleImprove} disabled={loading || !content} className="ai-btn green">
              {loading ? '⏳ Processing...' : '🔧 Apply'}
            </button>
          </div>

          {/* Translate Panel */}
          <div className="ai-glass-panel">
            <h3 className="amber">🌍 Translate</h3>
            <div className="ai-form-group">
              <label className="ai-label">Target Language</label>
              <select value={language} onChange={e => setLanguage(e.target.value)} className="ai-select">
                {['English','French','Spanish','Portuguese','Arabic','Chinese','German','Japanese','Swahili','Hindi'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button onClick={handleTranslate} disabled={loading || !content} className="ai-btn amber">
              {loading ? '⏳ Translating...' : '🌍 Translate'}
            </button>
          </div>

          {/* Export */}
          {content && (
            <div className="ai-glass-panel compact">
              <h3 className="purple">📤 Export</h3>
              <div className="ai-export-row">
                <button onClick={copyContent} className="ai-btn neutral inline">📋 Copy</button>
                <button onClick={printContent} className="ai-btn neutral inline">🖨️ Print/PDF</button>
              </div>
            </div>
          )}
        </div>

        {/* Right Panel — Editor */}
        <div className="ai-glass-panel ai-studio-editor-area">
          <div className="ai-editor-top-bar">
            <h3>📝 Content Editor</h3>
            {statusMsg && <span className={`ai-status ${statusClass}`}>{statusMsg}</span>}
          </div>
          <textarea
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Start typing, or click 'Generate' to create content with AI..."
            className="ai-editor-textarea"
          />
          <div className="ai-editor-footer">{content.length} chars · {content.split(/\s+/).filter(Boolean).length} words</div>
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   SLIDE BUILDER TAB
   ================================================================ */
const SlideBuilderTab = ({ API_BASE, token }) => {
  const [topic, setTopic] = useState('');
  const [numSlides, setNumSlides] = useState(10);
  const [audience, setAudience] = useState('learners');
  const [level, setLevel] = useState('intermediate');
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [showNotes, setShowNotes] = useState(false);

  const generateSlides = async () => {
    if (!topic.trim()) return setStatusMsg('⚠️ Enter a topic');
    setLoading(true); setStatusMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/creator/ai/slides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ topic, numSlides, audience, level }),
      });
      const data = await res.json();
      if (data.success && data.slides?.length) {
        setSlides(data.slides); setCurrent(0); setStatusMsg(`✅ ${data.slides.length} slides generated!`);
      } else setStatusMsg('❌ ' + (data.message || 'Generation failed'));
    } catch { setStatusMsg('❌ Network error'); }
    finally { setLoading(false); }
  };

  const printDeck = () => {
    const win = window.open();
    win.document.write(`<html><head><style>
      body{font-family:'Inter',sans-serif;margin:0;background:#0f172a;color:#e2e8f0}
      .slide{min-height:100vh;display:flex;flex-direction:column;justify-content:center;align-items:center;padding:60px;page-break-after:always}
      h1{font-size:48px;background:linear-gradient(135deg,#6366f1,#a78bfa);-webkit-background-clip:text;-webkit-text-fill-color:transparent;text-align:center}
      h2{font-size:32px;color:#a78bfa;margin-bottom:24px}
      ul{font-size:22px;line-height:2;max-width:800px;list-style:none;padding:0}
      li::before{content:'▸ ';color:#a78bfa;font-weight:bold}
      .num{font-size:14px;color:#475569;position:absolute;top:20px;right:30px}
    </style></head><body>
      ${slides.map((s,i) => `<div class="slide"><div class="num">${i+1}/${slides.length}</div><h1>${s.title}</h1>${s.subtitle?`<h2>${s.subtitle}</h2>`:''}<ul>${(s.bullets||[]).map(b=>`<li>${b}</li>`).join('')}</ul></div>`).join('')}
    </body></html>`);
    win.print(); win.close();
  };

  const slide = slides[current];
  const typeClass = `type-${slide?.type || 'content'}`;

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <div className="ai-tab-icon cyan">📊</div>
        <div>
          <h2 className="ai-tab-title cyan">Slide Builder</h2>
          <p className="ai-tab-subtitle">Generate a full slide deck with AI in seconds</p>
        </div>
      </div>

      <div className="slide-builder-grid">
        {/* Controls */}
        <div className="slide-builder-sidebar">
          <div className="ai-glass-panel">
            <h3 className="cyan">⚙️ Configuration</h3>
            <div className="ai-form-group">
              <label className="ai-label">Topic</label>
              <input value={topic} onChange={e => setTopic(e.target.value)} placeholder="e.g. Machine Learning Fundamentals" className="ai-input" />
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Number of Slides</label>
              <input type="number" min={5} max={30} value={numSlides} onChange={e => setNumSlides(Number(e.target.value))} className="ai-input" />
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Audience</label>
              <input value={audience} onChange={e => setAudience(e.target.value)} placeholder="e.g. university students" className="ai-input" />
            </div>
            <div className="ai-form-group">
              <label className="ai-label">Level</label>
              <select value={level} onChange={e => setLevel(e.target.value)} className="ai-select">
                {['beginner','intermediate','advanced'].map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <button onClick={generateSlides} disabled={loading} className="ai-btn cyan">
              {loading ? '⏳ Generating...' : '✨ Generate Slides'}
            </button>
            {statusMsg && <p style={{ fontSize: '0.8rem', marginTop: 10, color: statusMsg.startsWith('✅') ? '#34d399' : '#f87171' }}>{statusMsg}</p>}
          </div>

          {slides.length > 0 && (
            <div className="ai-glass-panel">
              <h3 className="purple">📋 Slide List</h3>
              <div className="slide-list-scroll">
                {slides.map((s,i) => (
                  <button key={i} onClick={() => setCurrent(i)} className={`slide-list-item ${i===current ? 'active' : ''}`}>
                    <span className="slide-list-num">{i+1}.</span>{s.title}
                  </button>
                ))}
              </div>
              <button onClick={printDeck} className="ai-btn neutral compact">🖨️ Export PDF</button>
              <button onClick={() => setShowNotes(!showNotes)} className="ai-btn neutral compact">
                {showNotes ? '🔇 Hide Notes' : '📝 Speaker Notes'}
              </button>
            </div>
          )}
        </div>

        {/* Slide Canvas */}
        <div>
          {slides.length === 0 ? (
            <div className="ai-empty-state">
              <span className="ai-empty-icon">📊</span>
              <p className="ai-empty-text">Fill in the topic and click "Generate Slides" to create a professional presentation</p>
            </div>
          ) : (
            <>
              <div className={`slide-canvas ${typeClass}`}>
                <span className="slide-counter">{current+1} / {slides.length}</span>
                <h1 className="slide-title">{slide?.title}</h1>
                {slide?.subtitle && <h2 className="slide-subtitle">{slide.subtitle}</h2>}
                {slide?.bullets?.length > 0 && (
                  <ul className="slide-bullets">
                    {slide.bullets.map((b,i) => (
                      <li key={i} className="slide-bullet-item">
                        <span className="slide-bullet-marker">▸</span>{b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {showNotes && slide?.speakerNotes && (
                <div className="slide-speaker-notes">
                  <strong>🎤 Speaker Notes: </strong>{slide.speakerNotes}
                </div>
              )}

              <div className="slide-nav-row">
                <button onClick={() => setCurrent(Math.max(0,current-1))} disabled={current===0} className="slide-nav-btn">◀ Prev</button>
                <button onClick={() => setCurrent(Math.min(slides.length-1,current+1))} disabled={current===slides.length-1} className="slide-nav-btn">Next ▶</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   VIDEO LAB TAB
   ================================================================ */
const VideoLabTab = ({ API_BASE, token }) => {
  const [file, setFile] = useState(null);
  const [transcript, setTranscript] = useState('');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [scriptTopic, setScriptTopic] = useState('');
  const [generatedScript, setGeneratedScript] = useState('');
  const [scriptLoading, setScriptLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dropRef = useRef(null);

  const handleFileDrop = e => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer?.files[0] || e.target.files?.[0];
    if (f) setFile(f);
  };

  const transcribeFile = async () => {
    if (!file) return setStatusMsg('⚠️ Drop or select a media file first');
    setLoading(true); setStatusMsg(''); setTranscript('');
    try {
      const isImage = file.type.startsWith('image/');
      if (isImage) {
        // Images are small enough to send as base64
        const reader = new FileReader();
        reader.onload = async ev => {
          const base64 = ev.target.result;
          const res = await fetch(`${API_BASE}/api/creator/ai/transcribe`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ base64, type: file.type, name: file.name }),
          });
          const data = await res.json();
          if (data.success) { setTranscript(data.transcript); setStatusMsg('✅ Analysis complete!'); }
          else setStatusMsg('❌ ' + (data.message || 'Analysis failed'));
          setLoading(false);
        };
        reader.readAsDataURL(file);
      } else {
        // Audio/Video — don't upload the raw file (would exceed 10MB body limit).
        // Send only metadata; the backend returns AI transcription guidance.
        const res = await fetch(`${API_BASE}/api/creator/ai/transcribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ type: file.type, name: file.name }),
        });
        const data = await res.json();
        if (data.success) { setTranscript(data.transcript); setStatusMsg('✅ Transcription guidance ready!'); }
        else setStatusMsg('❌ ' + (data.message || 'Service error'));
        setLoading(false);
      }
    } catch { setStatusMsg('❌ Error processing file'); setLoading(false); }
  };

  const generateScript = async () => {
    if (!scriptTopic.trim()) return;
    setScriptLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/creator/ai/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type: 'script', topic: scriptTopic, options: { level: 'intermediate', audience: 'learners' } }),
      });
      const data = await res.json();
      if (data.success) setGeneratedScript(data.content);
    } catch {} finally { setScriptLoading(false); }
  };

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <div className="ai-tab-icon amber">🎬</div>
        <div>
          <h2 className="ai-tab-title amber">Video Lab</h2>
          <p className="ai-tab-subtitle">Transcribe audio/video with AI · Generate video scripts</p>
        </div>
      </div>

      <div className="video-lab-grid">
        {/* Transcription */}
        <div className="ai-glass-panel">
          <h3 className="amber">🎤 Transcribe Audio / Video</h3>
          <div
            ref={dropRef}
            className={`drop-zone ${dragOver ? 'drag-over' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleFileDrop}
            onClick={() => document.getElementById('videoFileInput').click()}
          >
            <span className="drop-zone-icon">📁</span>
            <p className="drop-zone-text">{file ? `📎 ${file.name}` : 'Drop video or audio file here'}</p>
            <p className="drop-zone-hint">MP4, MP3, WAV, M4A, MOV supported</p>
            <input id="videoFileInput" type="file" accept="video/*,audio/*" style={{ display: 'none' }} onChange={handleFileDrop} />
          </div>
          {file && (
            <div>
              <div className="file-info-bar">
                <span className="file-info-name">📎 {file.name}</span>
                <span className="file-info-size">{(file.size / 1024 / 1024).toFixed(1)} MB</span>
              </div>
              <button onClick={transcribeFile} disabled={loading} className="ai-btn amber">
                {loading ? '⏳ Transcribing...' : '🎙️ Transcribe Now'}
              </button>
            </div>
          )}
          {statusMsg && <p style={{ fontSize: '0.82rem', color: statusMsg.startsWith('✅') ? '#34d399' : '#f87171', marginTop: 14 }}>{statusMsg}</p>}
          {transcript && (
            <div className="transcript-output">
              <div className="transcript-header">
                <h4>Transcript</h4>
                <button onClick={() => navigator.clipboard.writeText(transcript)} className="ai-btn neutral inline">📋 Copy</button>
              </div>
              <textarea readOnly value={transcript} className="transcript-textarea" />
            </div>
          )}
        </div>

        {/* Script Generator */}
        <div className="ai-glass-panel">
          <h3 className="purple">📜 Video Script Generator</h3>
          <div className="ai-form-group">
            <label className="ai-label">Video Topic</label>
            <input value={scriptTopic} onChange={e => setScriptTopic(e.target.value)} placeholder="e.g. 5 tips to learn Python faster" className="ai-input" />
          </div>
          <button onClick={generateScript} disabled={scriptLoading || !scriptTopic} className="ai-btn purple">
            {scriptLoading ? '⏳ Writing script...' : '✍️ Generate Script'}
          </button>
          {generatedScript ? (
            <div className="transcript-output">
              <div className="transcript-header">
                <h4>Script</h4>
                <button onClick={() => navigator.clipboard.writeText(generatedScript)} className="ai-btn neutral inline">📋 Copy</button>
              </div>
              <textarea value={generatedScript} onChange={e => setGeneratedScript(e.target.value)} className="transcript-textarea" style={{ minHeight: 380 }} />
            </div>
          ) : (
            <div className="script-empty">
              <span className="ai-empty-icon" style={{ fontSize: '3rem' }}>🎬</span>
              <p className="ai-empty-text" style={{ marginTop: 12 }}>Enter a topic to generate a professional video script with intro, main points, and outro.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ================================================================
   MENTORING HUB TAB
   ================================================================ */
const MentoringHubTab = ({ API_BASE, token, user }) => {
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [announcement, setAnnouncement] = useState('');
  const [announceSent, setAnnounceSent] = useState(false);
  const [tab, setTab] = useState('messages');

  useEffect(() => {
    fetch(`${API_BASE}/api/messages/conversations`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => { if (data.success) setThreads(data.conversations || []); })
      .catch(() => {});
  }, [API_BASE, token]);

  const loadThread = async (userId) => {
    setSelectedThread(userId); setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/messages/${userId}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.success) setMessages(data.messages || []);
    } catch {} finally { setLoading(false); }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selectedThread) return;
    try {
      await fetch(`${API_BASE}/api/messages/${selectedThread}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: reply }),
      });
      setMessages(prev => [...prev, { sender: { _id: user?._id, name: 'You' }, content: reply, createdAt: new Date() }]);
      setReply('');
    } catch {}
  };

  const draftAIReply = async () => {
    const lastStudentMsg = [...messages].reverse().find(m => m.sender?._id !== user?._id);
    if (!lastStudentMsg) return;
    setDraftLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/creator/ai/improve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: lastStudentMsg.content, mode: 'reply', context: 'instructor responding to student', language: 'English' }),
      });
      const data = await res.json();
      if (data.success) setReply(data.content);
    } catch {} finally { setDraftLoading(false); }
  };

  const avatarGradients = ['linear-gradient(135deg,#6366f1,#8b5cf6)', 'linear-gradient(135deg,#10b981,#059669)', 'linear-gradient(135deg,#f59e0b,#ef4444)', 'linear-gradient(135deg,#ec4899,#a855f7)', 'linear-gradient(135deg,#0ea5e9,#06b6d4)'];

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <div className="ai-tab-icon green">🧑‍🏫</div>
        <div>
          <h2 className="ai-tab-title green">Mentoring Hub</h2>
          <p className="ai-tab-subtitle">Connect with students · AI-assisted replies · Broadcast announcements</p>
        </div>
      </div>

      <div className="mentoring-subtab-row">
        {['messages','announcements'].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`mentoring-subtab ${tab===t ? 'active' : ''}`}>
            {t === 'messages' ? '💬 Student Messages' : '📢 Announcements'}
          </button>
        ))}
      </div>

      {tab === 'messages' && (
        <div className="mentoring-messages-grid">
          {/* Thread List */}
          <div className="ai-glass-panel thread-list-panel">
            <h4 className="thread-list-heading">Conversations</h4>
            {threads.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32 }}>
                <span style={{ fontSize: '2.5rem' }}>💬</span>
                <p style={{ color: '#475569', fontSize: '0.82rem', marginTop: 12 }}>No student messages yet</p>
              </div>
            ) : threads.map((t, i) => (
              <button key={i} onClick={() => loadThread(t.userId)} className={`thread-item ${selectedThread===t.userId ? 'selected' : ''}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="thread-avatar" style={{ background: avatarGradients[i % avatarGradients.length] }}>
                    {t.name?.[0] || '?'}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <p className="thread-name">{t.name || 'Student'}</p>
                    <p className="thread-preview">{t.lastMessage?.substring(0,40) || 'No messages'}...</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Message Thread */}
          <div className="ai-glass-panel message-panel">
            {!selectedThread ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: 12 }}>
                <span style={{ fontSize: '3rem' }}>👆</span>
                <p className="ai-empty-text">Select a conversation to view messages</p>
              </div>
            ) : (
              <>
                <div className="message-panel-header">
                  <h4>💬 Conversation</h4>
                </div>
                <div className="message-list">
                  {loading ? <p style={{ color: '#475569', textAlign: 'center' }}>Loading...</p> : messages.map((m, i) => {
                    const isMe = m.sender?._id === user?._id || m.sender?.name === 'You';
                    return (
                      <div key={i} className={`message-bubble ${isMe ? 'sent' : 'received'}`}>
                        <p className="message-text">{m.content}</p>
                        <p className="message-time">{new Date(m.createdAt).toLocaleTimeString()}</p>
                      </div>
                    );
                  })}
                </div>
                <div className="message-compose-area">
                  <div className="message-compose-row">
                    <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." rows={2} />
                    <div className="message-compose-btns">
                      <button onClick={sendReply} disabled={!reply.trim()} className="ai-btn green inline">Send</button>
                      <button onClick={draftAIReply} disabled={draftLoading} className="ai-btn neutral inline">{draftLoading ? '...' : '🤖 AI Draft'}</button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {tab === 'announcements' && (
        <div className="ai-glass-panel announcement-panel">
          <h3 className="green">📢 Broadcast Announcement</h3>
          <p>Send a message to all your enrolled students at once.</p>
          <div className="ai-form-group">
            <label className="ai-label">Announcement Message</label>
            <textarea value={announcement} onChange={e => setAnnouncement(e.target.value)} rows={6} placeholder="e.g. New module released! Module 4 is now available. Check it out in your student portal." className="announcement-textarea" />
          </div>
          <button onClick={() => { setAnnounceSent(true); setTimeout(() => setAnnounceSent(false), 3000); setAnnouncement(''); }} disabled={!announcement.trim()} className="ai-btn green" style={{ maxWidth: 280 }}>
            📤 Send to All Students
          </button>
          {announceSent && <div className="announcement-success">✅ Announcement sent successfully!</div>}
        </div>
      )}
    </div>
  );
};

/* ================================================================
   CURRICULUM PLANNER TAB
   ================================================================ */
const CurriculumPlannerTab = ({ API_BASE, token }) => {
  const [form, setForm] = useState({ topic: '', audience: 'beginners', level: 'beginner', durationWeeks: 8, hoursPerWeek: 5 });
  const [curriculum, setCurriculum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [expandedWeek, setExpandedWeek] = useState(null);

  const generate = async () => {
    if (!form.topic.trim()) return setStatusMsg('⚠️ Enter a topic');
    setLoading(true); setStatusMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/creator/ai/curriculum`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) { setCurriculum(data.curriculum); setStatusMsg('✅ Curriculum generated!'); setExpandedWeek(0); }
      else setStatusMsg('❌ ' + (data.message || 'Failed'));
    } catch { setStatusMsg('❌ Network error'); }
    finally { setLoading(false); }
  };

  const exportCurriculum = () => {
    if (!curriculum) return;
    const lines = [`# ${curriculum.courseTitle}`, '', curriculum.courseDescription, '', '## Learning Outcomes', ...(curriculum.learningOutcomes||[]).map(o => `- ${o}`), '', '## Prerequisites', ...(curriculum.prerequisites||[]).map(p => `- ${p}`), '', '---'];
    (curriculum.weeks || []).forEach(w => {
      lines.push(`\n## Week ${w.week}: ${w.theme}`, '', '**Objectives:**', ...(w.objectives||[]).map(o => `- ${o}`), '', '**Modules:**', ...(w.modules||[]).map(m => `- [${m.type?.toUpperCase()}] ${m.title} (${m.duration}) — ${m.description}`), '', `**Assignment:** ${w.assignment}`, '', '**Resources:**', ...(w.resources||[]).map(r => `- ${r}`));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${form.topic.replace(/\s+/g,'-')}-curriculum.md`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ai-tab-container">
      <div className="ai-tab-header">
        <div className="ai-tab-icon pink">🗺️</div>
        <div>
          <h2 className="ai-tab-title pink">Curriculum Planner</h2>
          <p className="ai-tab-subtitle">Generate a complete course curriculum with weekly modules and assignments</p>
        </div>
      </div>

      <div className={`curriculum-grid ${curriculum ? 'has-result' : 'no-result'}`}>
        {/* Form */}
        <div className="ai-glass-panel" style={{ alignSelf: 'start' }}>
          <h3 className="pink">⚙️ Course Setup</h3>
          <div className="ai-form-group">
            <label className="ai-label">Course Topic</label>
            <input value={form.topic} onChange={e => setForm(p=>({...p, topic: e.target.value}))} placeholder="e.g. Full-Stack Web Development" className="ai-input" />
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Target Audience</label>
            <input value={form.audience} onChange={e => setForm(p=>({...p, audience: e.target.value}))} placeholder="e.g. career switchers, engineers" className="ai-input" />
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Level</label>
            <select value={form.level} onChange={e => setForm(p=>({...p, level: e.target.value}))} className="ai-select">
              {['beginner','intermediate','advanced'].map(l=><option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Duration (weeks)</label>
            <input type="number" min={2} max={52} value={form.durationWeeks} onChange={e => setForm(p=>({...p, durationWeeks: Number(e.target.value)}))} className="ai-input" />
          </div>
          <div className="ai-form-group">
            <label className="ai-label">Hours per week</label>
            <input type="number" min={1} max={40} value={form.hoursPerWeek} onChange={e => setForm(p=>({...p, hoursPerWeek: Number(e.target.value)}))} className="ai-input" />
          </div>
          <button onClick={generate} disabled={loading} className="ai-btn pink">
            {loading ? '⏳ Building curriculum...' : '🗺️ Generate Curriculum'}
          </button>
          {statusMsg && <p style={{ fontSize: '0.8rem', marginTop: 10, color: statusMsg.startsWith('✅') ? '#34d399' : '#f87171' }}>{statusMsg}</p>}
          {curriculum && (
            <button onClick={exportCurriculum} className="ai-btn neutral compact">⬇️ Export Markdown</button>
          )}
        </div>

        {/* Curriculum Output */}
        {curriculum ? (
          <div>
            {/* Header Card */}
            <div className="curriculum-header-card ai-glass-panel" style={{ background: 'linear-gradient(135deg, rgba(236,72,153,0.2), rgba(168,85,247,0.15))' }}>
              <h2>{curriculum.courseTitle}</h2>
              <p>{curriculum.courseDescription}</p>
              <div className="curriculum-badges">
                <span className="curriculum-badge">{form.durationWeeks} weeks</span>
                <span className="curriculum-badge">{form.hoursPerWeek} hrs/week</span>
                <span className="curriculum-badge">{form.level}</span>
              </div>
            </div>

            {/* Outcomes + Prerequisites */}
            <div className="curriculum-two-col">
              <div className="ai-glass-panel compact curriculum-info-card">
                <h4 className="green">🎯 Learning Outcomes</h4>
                <ul className="curriculum-info-list">
                  {(curriculum.learningOutcomes||[]).map((o,i) => <li key={i}><span className="icon green">✓</span>{o}</li>)}
                </ul>
              </div>
              <div className="ai-glass-panel compact curriculum-info-card">
                <h4 className="amber">📋 Prerequisites</h4>
                <ul className="curriculum-info-list">
                  {(curriculum.prerequisites||[]).map((p,i) => <li key={i}><span className="icon amber">→</span>{p}</li>)}
                </ul>
              </div>
            </div>

            {/* Weekly Plan */}
            <h3 className="weekly-heading">📅 Weekly Plan</h3>
            {(curriculum.weeks||[]).map((w,i) => (
              <div key={i} className="week-accordion ai-glass-panel" style={{ padding: 0 }}>
                <button onClick={() => setExpandedWeek(expandedWeek===i ? null : i)} className="week-header-btn">
                  <div className="week-header-left">
                    <span className="week-number-badge">W{w.week}</span>
                    <span className="week-theme-name">{w.theme}</span>
                  </div>
                  <span className="week-chevron">{expandedWeek===i ? '▲' : '▼'}</span>
                </button>
                {expandedWeek===i && (
                  <div className="week-body">
                    {w.objectives?.length > 0 && (
                      <div>
                        <p className="week-section-label">Objectives</p>
                        {w.objectives.map((o,j) => <p key={j} className="week-objective">{o}</p>)}
                      </div>
                    )}
                    {w.modules?.length > 0 && (
                      <div>
                        <p className="week-section-label">Modules</p>
                        <div className="week-modules-list">
                          {w.modules.map((m,j) => (
                            <div key={j} className="week-module-card">
                              <span className={`module-type-badge ${m.type || 'lecture'}`}>{m.type?.toUpperCase() || 'LECTURE'}</span>
                              <div className="module-info">
                                <p className="module-title">{m.title}</p>
                                <p className="module-desc">{m.description}</p>
                              </div>
                              <span className="module-duration">{m.duration}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {w.assignment && <p className="week-assignment">📝 <strong>Assignment:</strong> {w.assignment}</p>}
                    {w.resources?.length > 0 && (
                      <div className="week-resources">
                        {w.resources.map((r,j) => <span key={j} className="week-resource-tag">📌 {r}</span>)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="ai-empty-state">
            <span className="ai-empty-icon">🗺️</span>
            <p className="ai-empty-text">Fill in the course details and click "Generate Curriculum" to build a full week-by-week learning plan.</p>
          </div>
        )}
      </div>
    </div>
  );
};



const ContentCreatorDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [myCourses, setMyCourses] = useState([]);
  const [students, setStudents] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [contentLibrary, setContentLibrary] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [enrollments, setEnrollments] = useState([]); // Added for demo enrollments
  const [courseFormData, setCourseFormData] = useState({
    title: '',
    description: '',
    category: '',
    level: 'Beginner',
    price: 0,
    duration: '',
    thumbnail: null,
    displayPage: 'courses'
  });
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [showContentModal, setShowContentModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState(null);
  const [isLightMode, setIsLightMode] = useState(() => localStorage.getItem('creatorTheme') === 'light');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 768
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (user && user.role !== 'instructor' && user.role !== 'admin') {
      setError('Access denied. This dashboard is only available to instructors and administrators.');
      setLoading(false);
      return;
    }
    
    initializeDashboard();
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    localStorage.setItem('creatorTheme', isLightMode ? 'light' : 'dark');
  }, [isLightMode]);

  useEffect(() => {
    if (activeTab === 'messaging') {
      setActiveTab('overview');
    }
  }, [activeTab]);

  const initializeDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      // Run all fetch operations concurrently, but don't let one failure stop others
      const results = await Promise.allSettled([
        fetchCreatorData(),
        fetchMyCourses(),
        fetchStudents(),
        fetchEarnings(),
        fetchContentLibrary(),
        fetchAnalytics(),
        fetchRecentActivity(),
        fetchNotifications()
      ]);

      // Load demo enrollments
      loadDemoEnrollments();

      // Check if any critical operations failed
      const failed = results.filter(result => result.status === 'rejected');
      if (failed.length > 0) {
        console.warn('Some dashboard operations failed:', failed);
      }
    } catch (error) {
      console.error('Dashboard initialization error:', error);
      setError('Some dashboard data could not be loaded. You can still use the available features.');
    } finally {
      setLoading(false);
    }
  };

  const fetchCreatorData = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/creator/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized - token may be invalid');
          logout();
          return;
        } else if (response.status === 404) {
          console.error('Creator dashboard endpoint not found');
          setDashboardData(null);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setDashboardData(data.data);
      } else {
        console.error('Error fetching creator data:', data.message);
        setDashboardData(null);
      }
    } catch (error) {
      console.error('Error fetching creator data:', error);
      setDashboardData(null);
      // Don't rethrow since this is optional data
    }
  };

  const fetchMyCourses = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        setMyCourses([]);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/creator/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized - token may be invalid');
          logout();
          return;
        } else if (response.status === 404) {
          console.error('Creator courses endpoint not found');
          setMyCourses([]);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setMyCourses(Array.isArray(data.data.courses) ? data.data.courses : []);
      } else {
        console.error('Error fetching courses:', data.message);
        setMyCourses([]);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      setMyCourses([]);
      // Don't rethrow since this is optional data
    }
  };

  const fetchStudents = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        setStudents([]);
        return;
      }

      // Get enrollments for the instructor's courses
      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/enrollments?instructor=me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized - token may be invalid');
          logout();
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // Extract unique students from enrollments
        const uniqueStudents = [];
        const studentIds = new Set();
        data.data.forEach(enrollment => {
          if (enrollment.student && !studentIds.has(enrollment.student._id)) {
            studentIds.add(enrollment.student._id);
            uniqueStudents.push({
              ...enrollment.student,
              enrolledCourses: data.data.filter(e => e.student._id === enrollment.student._id).length,
              progress: enrollment.progress || 0
            });
          }
        });
        setStudents(uniqueStudents);
      } else {
        console.error('Error fetching students:', data.message || 'Invalid data format');
        setStudents([]);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      setStudents([]);
      // Don't rethrow since this is optional data
    }
  };

  const fetchEarnings = async () => {
    try {
      // Earnings data will come from the dashboard endpoint
      // For now, keep mock data since earnings calculations are complex
      setEarnings({
        total: 0,
        thisMonth: 0,
        lastMonth: 0,
        pending: 0,
        monthlyRevenue: []
      });
    } catch (error) {
      console.error('Error fetching earnings:', error);
      setEarnings({
        total: 0,
        thisMonth: 0,
        lastMonth: 0,
        pending: 0,
        monthlyRevenue: []
      });
      throw error;
    }
  };

  const fetchContentLibrary = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        console.error('No auth token found');
        setContentLibrary([]);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/creator/lessons`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.error('Unauthorized - token may be invalid');
          logout();
          return;
        } else if (response.status === 404) {
          console.error('Creator lessons endpoint not found');
          setContentLibrary([]);
          return;
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success && Array.isArray(data.data)) {
        // Transform lessons data to content library format
        const content = data.data.map(lesson => ({
          id: lesson._id,
          name: lesson.title,
          type: lesson.type,
          duration: lesson.estimatedDuration ? `${lesson.estimatedDuration} mins` : 'N/A',
          uploadDate: new Date(lesson.createdAt).toLocaleDateString(),
          course: lesson.course?.title || 'Unknown Course',
          status: lesson.status || 'draft'
        }));
        setContentLibrary(content);
      } else {
        console.error('Error fetching content library:', data.message || 'Invalid data format');
        setContentLibrary([]);
      }
    } catch (error) {
      console.error('Error fetching content library:', error);
      setContentLibrary([]);
      // Don't rethrow since this is optional data
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Analytics data should come from dashboard endpoint
      // Extract from dashboardData if available, otherwise use defaults
      if (dashboardData?.courseAnalytics) {
        setAnalytics(dashboardData.courseAnalytics);
      } else {
        setAnalytics({
          courseViews: { total: 0, thisWeek: 0, growth: 0 },
          enrollment: { total: 0, thisWeek: 0, growth: 0 },
          completion: { rate: 0, improvement: 0 },
          engagement: { avgTimeSpent: '0 mins', quizScores: 0, feedback: 0 },
          topCourses: []
        });
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setAnalytics({
        courseViews: { total: 0, thisWeek: 0, growth: 0 },
        enrollment: { total: 0, thisWeek: 0, growth: 0 },
        completion: { rate: 0, improvement: 0 },
        engagement: { avgTimeSpent: '0 mins', quizScores: 0, feedback: 0 },
        topCourses: []
      });
      throw error;
    }
  };

  const fetchRecentActivity = async () => {
    try {
      // Recent activity should come from dashboard endpoint
      if (dashboardData?.recentEnrollments) {
        const activities = dashboardData.recentEnrollments.map((enrollment, index) => ({
          id: index + 1,
          type: 'enrollment',
          message: `${enrollment.student?.firstName || 'Student'} enrolled in ${enrollment.course?.title || 'a course'}`,
          timestamp: new Date(enrollment.createdAt),
          icon: '�'
        }));
        setRecentActivity(activities);
      } else {
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('Error fetching recent activity:', error);
      setRecentActivity([]);
      throw error;
    }
  };

  const fetchNotifications = async () => {
    try {
      // Notifications should come from dashboard endpoint or a separate notifications API
      if (dashboardData?.pendingReviews) {
        const notifs = dashboardData.pendingReviews.map((review, index) => ({
          id: index + 1,
          type: 'info',
          title: 'Pending Review',
          message: `Course "${review.course?.title || 'Unknown'}" is waiting for review`,
          timestamp: new Date(review.createdAt),
          read: false
        }));
        setNotifications(notifs);
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]);
      throw error;
    }
  };

  // Load demo enrollments from localStorage
  const loadDemoEnrollments = async () => {
    try {
      const allEnrollments = [];
      
      // First, try to load from database with instructor filter
      try {
        const token = localStorage.getItem('authToken');
        if (token) {
          // For instructors, use instructor=me to get enrollments in their courses
          const response = await fetch(`${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/enrollments?instructor=me&limit=1000`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            if (result.success && result.data?.enrollments) {
              console.log('📊 Creator: Loaded enrollments from database:', result.data.enrollments.length);
              allEnrollments.push(...result.data.enrollments);
            }
          } else {
            console.warn('Failed to load enrollments from database:', response.status, response.statusText);
          }
        }
      } catch (dbError) {
        console.warn('Could not load from database:', dbError);
      }
      
      // Also scan localStorage for instructor-specific enrollment keys
      const userEmail = user?.email || '';
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('demoEnrollments_')) {
          try {
            const enrollmentsData = JSON.parse(localStorage.getItem(key) || '[]');
            // Only add if not already in database results and belongs to this instructor
            enrollmentsData.forEach(localEnroll => {
              const existsInDb = allEnrollments.some(dbEnroll => 
                dbEnroll._id === localEnroll._id || 
                dbEnroll.enrollmentCode === localEnroll.enrollmentCode
              );
              
              // Check if this enrollment is for a course by this instructor
              const isMyEnrollment = localEnroll.course?.instructor?.email === userEmail ||
                                     localEnroll.course?.createdBy === user?.id;
              
              if (!existsInDb && isMyEnrollment) {
                allEnrollments.push(localEnroll);
              }
            });
          } catch (e) {
            console.error(`Error parsing enrollments from key ${key}:`, e);
          }
        }
      }
      
      console.log('📊 Creator: Total enrollments in my courses:', allEnrollments.length);
      setEnrollments(allEnrollments);
      return allEnrollments;
    } catch (error) {
      console.error('Error loading demo enrollments:', error);
      setEnrollments([]);
      return [];
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Course Management Functions
  const handleCreateCourse = async (e) => {
    e.preventDefault();
    try {
      // Check if we're editing or creating
      const isEditing = courseFormData.courseId;
      
      // Map frontend fields to backend expected fields
      const courseData = {
        title: courseFormData.title,
        description: courseFormData.description,
        category: courseFormData.category,
        level: courseFormData.level,
        estimatedDuration: parseInt(parseFloat(courseFormData.duration) * 60) || 60, // Convert hours to minutes
        price: parseFloat(courseFormData.price) || 0,
        displayPage: courseFormData.displayPage || 'courses'
      };

      // Validate required fields before sending
      if (!courseData.title || !courseData.description || !courseData.category) {
        alert('Please fill in all required fields (title, description, category)');
        return;
      }

      console.log(isEditing ? 'Updating course with data:' : 'Creating course with data:', courseData);

      const token = localStorage.getItem('authToken');
      if (!token) {
        alert('No authentication token found. Please login again.');
        return;
      }

      const endpoint = isEditing 
        ? `${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/creator/courses/${courseFormData.courseId}`
        : `${process.env.REACT_APP_API_BASE || 'https://qorenova-backend-production.up.railway.app'}/api/creator/courses`;
      const method = isEditing ? 'PUT' : 'POST';

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(courseData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      const data = await response.json();
      console.log('Response data:', data);
      
      if (response.ok && data.success) {
        // Refresh courses list
        fetchMyCourses();
        setShowCourseModal(false);
        setCourseFormData({
          title: '',
          description: '',
          category: '',
          level: 'Beginner',
          price: 0,
          duration: '',
          thumbnail: null,
          displayPage: 'courses'
        });
        alert(isEditing ? 'Course updated successfully!' : 'Course created successfully!');
      } else {
        // Handle validation errors
        if (response.status === 400 && data.errors) {
          const errorMessages = data.errors.map(err => err.msg).join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert(`Error: ${data.message || `Failed to ${isEditing ? 'update' : 'create'} course`}`);
        }
      }
    } catch (error) {
      console.error('Error saving course:', error);
      alert('Error saving course. Please try again.');
    }
  };

  const handleCourseAction = async (courseId, action) => {
    try {
      const token = localStorage.getItem('authToken');
      let endpoint = '';
      let method = 'PUT';
      let body = {};

      switch (action) {
        case 'edit':
          // Find the course to edit
          const courseToEdit = myCourses.find(c => c._id === courseId);
          if (courseToEdit) {
            // Populate the form with existing course data
            setCourseFormData({
              title: courseToEdit.title,
              description: courseToEdit.description,
              category: courseToEdit.category,
              level: courseToEdit.level,
              price: courseToEdit.pricing?.price || courseToEdit.price || 0,
              duration: courseToEdit.totalDuration ? (courseToEdit.totalDuration / 60).toFixed(1) : '',
              thumbnail: courseToEdit.thumbnail || null,
              displayPage: courseToEdit.displayPage || 'courses',
              courseId: courseId // Track which course we're editing
            });
            setShowCourseModal(true); // Open the modal
          }
          return;
        case 'submit-for-approval':
          endpoint = `/api/creator/courses/${courseId}/submit-for-approval`;
          method = 'POST';
          body = {};
          break;
        case 'publish':
          endpoint = `/api/creator/courses/${courseId}`;
          body = { status: 'published' };
          break;
        case 'unpublish':
          endpoint = `/api/creator/courses/${courseId}`;
          body = { status: 'draft' };
          break;
        case 'delete':
          if (!window.confirm('Are you sure you want to delete this course?')) {
            return;
          }
          endpoint = `/api/creator/courses/${courseId}`;
          method = 'DELETE';
          break;
        case 'analytics':
          alert(`Analytics for course ${courseId} - Would show detailed analytics`);
          return;
        default:
          return;
      }

      const response = await fetch(endpoint, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: method !== 'DELETE' ? JSON.stringify(body) : undefined
      });

      const data = await response.json();
      if (data.success) {
        fetchMyCourses(); // Refresh courses list
        
        // Custom success messages for different actions
        const successMessages = {
          'submit-for-approval': 'Course submitted for approval! You will be notified when it is reviewed.',
          'publish': 'Course published successfully!',
          'unpublish': 'Course unpublished successfully!',
          'delete': 'Course deleted successfully!'
        };
        
        alert(successMessages[action] || `Course ${action} successfully!`);
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error handling course action:', error);
      alert('Error processing request. Please try again.');
    }
  };

  // Content Management Functions
  const handleContentUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      // Mock upload - in real app would call /api/creator/content/upload
      const newContent = {
        id: Date.now(),
        name: formData.get('contentName'),
        type: formData.get('contentType'),
        size: '10 MB', // Mock size
        uploadDate: new Date().toISOString().split('T')[0],
        course: formData.get('course'),
        status: 'uploaded'
      };
      
      setContentLibrary(prev => [newContent, ...prev]);
      setShowContentModal(false);
      alert('Content uploaded successfully!');
    } catch (error) {
      console.error('Error uploading content:', error);
      alert('Error uploading content. Please try again.');
    }
  };

  const handleContentAction = (contentId, action) => {
    try {
      switch (action) {
        case 'edit':
          alert(`Edit content ${contentId} - Would open content editor`);
          break;
        case 'delete':
          if (window.confirm('Are you sure you want to delete this content?')) {
            setContentLibrary(prev => prev.filter(content => content.id !== contentId));
            alert('Content deleted successfully!');
          }
          break;
        case 'download':
          alert(`Download content ${contentId} - Would start download`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling content action:', error);
      alert('Error processing request. Please try again.');
    }
  };

  // Student Management Functions
  const handleStudentAction = (studentId, action) => {
    try {
      switch (action) {
        case 'message':
          alert(`Message student ${studentId} - Would open messaging interface`);
          break;
        case 'view':
          alert(`View student profile ${studentId} - Would show student details`);
          break;
        case 'progress':
          alert(`View progress for student ${studentId} - Would show progress tracking`);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error('Error handling student action:', error);
      alert('Error processing request. Please try again.');
    }
  };

  // Utility Functions
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: 'status-published',
      draft: 'status-draft',
      'pending-approval': 'status-pending-approval',
      rejected: 'status-rejected',
      pending: 'status-pending',
      archived: 'status-archived'
    };
    return badges[status] || 'status-default';
  };

  // Test API Functions
  const testApiEndpoint = async (endpoint) => {
    try {
      const response = await fetch(endpoint);
      const data = await response.json();
      alert(`API Test Result:\n\nEndpoint: ${endpoint}\nStatus: ${response.status}\nResponse: ${JSON.stringify(data, null, 2)}`);
    } catch (err) {
      alert(`API Test Failed:\n\nEndpoint: ${endpoint}\nError: ${err.message}`);
    }
  };

  const toggleThemeMode = () => {
    setIsLightMode(prev => !prev);
  };

  const studioTabs = {
    overview: { title: 'Overview', subtitle: 'Your teaching pulse across Academy' },
    courses: { title: 'Courses', subtitle: 'Create, edit, and publish your catalog' },
    students: { title: 'Students', subtitle: 'Who is learning from you' },
    analytics: { title: 'Analytics', subtitle: 'Views, completion, and ratings' },
    earnings: { title: 'Earnings', subtitle: 'Payouts and monthly revenue' },
    content: { title: 'Library', subtitle: 'Lessons, media, and reusable assets' },
    settings: { title: 'Settings', subtitle: 'Profile, notifications, and account' },
    'ai-studio': { title: 'AI Studio', subtitle: 'Generate and refine teaching content' },
    'slide-builder': { title: 'Slides', subtitle: 'Build decks from a topic' },
    'video-lab': { title: 'Video Lab', subtitle: 'Transcripts and scripts' },
    'mentoring-hub': { title: 'Mentoring', subtitle: 'Student threads and broadcasts' },
    'curriculum-planner': { title: 'Curriculum', subtitle: 'Week-by-week course plans' },
  };

  const studioNav = [
    {
      label: 'Studio',
      items: [
        { id: 'overview', label: 'Overview', icon: <LayoutDashboard size={18} /> },
        { id: 'courses', label: 'Courses', icon: <BookOpen size={18} />, count: myCourses.length },
        { id: 'students', label: 'Students', icon: <Users size={18} />, count: students.length },
        { id: 'analytics', label: 'Analytics', icon: <BarChart3 size={18} /> },
        { id: 'earnings', label: 'Earnings', icon: <DollarSign size={18} /> },
        { id: 'content', label: 'Library', icon: <FileText size={18} />, count: contentLibrary.length },
      ],
    },
    {
      label: 'AI Tools',
      items: [
        { id: 'ai-studio', label: 'AI Studio', icon: <Sparkles size={18} />, badge: 'AI' },
        { id: 'slide-builder', label: 'Slides', icon: <Presentation size={18} /> },
        { id: 'video-lab', label: 'Video Lab', icon: <Video size={18} /> },
        { id: 'mentoring-hub', label: 'Mentoring', icon: <GraduationCap size={18} /> },
        { id: 'curriculum-planner', label: 'Curriculum', icon: <Map size={18} /> },
      ],
    },
  ];

  const studioUser = {
    name: user?.name || 'Creator',
    avatar: (user?.name || 'C').slice(0, 1).toUpperCase(),
  };

  const goTab = (id) => {
    setActiveTab(id);
    setSidebarOpen(false);
  };

  if (loading) {
    return (
      <div className="adm-shell studio-loading">
        <RefreshCw className="spin" size={32} />
        <p>Loading Studio…</p>
      </div>
    );
  }

  if (error && error.includes('Access denied')) {
    return (
      <div className="adm-shell studio-loading">
        <p>{error}</p>
        <button type="button" className="studio-btn" onClick={() => navigate('/login')}>
          Sign in as instructor
        </button>
      </div>
    );
  }

  return (
    <AdminDashboardShell
      adminInfo={studioUser}
      activeTab={activeTab}
      activeTabMeta={studioTabs[activeTab]}
      navGroups={studioNav}
      onTabChange={goTab}
      sidebarCollapsed={sidebarCollapsed}
      onToggleCollapse={() => setSidebarCollapsed((c) => !c)}
      sidebarOpen={sidebarOpen}
      onToggleSidebar={() => setSidebarOpen((o) => !o)}
      isMobile={isMobile}
      onLogout={handleLogout}
      onRefresh={initializeDashboard}
      loading={loading}
      error={error}
      onDismissError={() => setError(null)}
      product={{
        mark: 'S',
        title: 'QoreNova',
        subtitle: 'Studio',
        role: user?.role === 'admin' ? 'Administrator' : 'Instructor',
        crumb: 'Studio',
        mobileEyebrow: 'Studio',
      }}
      crumbIcon={Sparkles}
      mobileNav={[
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'courses', label: 'Courses', icon: BookOpen },
      ]}
    >
      <div className="studio-page">
        {activeTab === 'overview' && (
          <div className="ultra-modern-overview">
            {/* Animated Background Elements */}
            <div className="overview-bg-elements">
              <div className="floating-shape shape-1"></div>
              <div className="floating-shape shape-2"></div>
              <div className="floating-shape shape-3"></div>
              <div className="floating-shape shape-4"></div>
            </div>

            {/* Hero Welcome Section with Glassmorphism */}
            <div className="hero-welcome-ultra">
              <div className="welcome-glass-card">
                <div className="welcome-content-wrapper">
                  <div className="welcome-icon-badge">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                  </div>
                  <div className="welcome-text-content">
                    <h1 className="ultra-gradient-title">
                      <span className="title-line">Welcome Back,</span>
                      <span className="title-emphasis">{user?.name || 'Creator'}</span>
                    </h1>
                    <p className="welcome-subtitle">
                      Your creative journey continues • Let's make today extraordinary
                    </p>
                  </div>
                </div>
                <div className="welcome-date-time">
                  <div className="date-display">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                      <line x1="16" y1="2" x2="16" y2="6"/>
                      <line x1="8" y1="2" x2="8" y2="6"/>
                      <line x1="3" y1="10" x2="21" y2="10"/>
                    </svg>
                    <span>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Stats Dashboard */}
            <div className="premium-stats-dashboard">
              <div className="stats-glass-container">
                {/* Primary Stat Card - Courses */}
                <div className="premium-stat-card stat-primary">
                  <div className="stat-card-glow"></div>
                  <div className="stat-card-content">
                    <div className="stat-icon-wrapper">
                      <div className="icon-bg-gradient primary-gradient"></div>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
                        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
                      </svg>
                    </div>
                    <div className="stat-details">
                      <h3 className="stat-value">{myCourses.length}</h3>
                      <p className="stat-label">Active Courses</p>
                      <div className="stat-meta">
                        <span className="meta-badge success">
                          {myCourses.filter(c => c.status === 'published').length} Published
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="stat-trend-indicator positive">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                    <span>+12%</span>
                  </div>
                </div>

                {/* Students Stat Card */}
                <div className="premium-stat-card stat-success">
                  <div className="stat-card-glow"></div>
                  <div className="stat-card-content">
                    <div className="stat-icon-wrapper">
                      <div className="icon-bg-gradient success-gradient"></div>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                        <circle cx="9" cy="7" r="4"/>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                      </svg>
                    </div>
                    <div className="stat-details">
                      <h3 className="stat-value">{dashboardData?.stats?.totalStudents || students.length}</h3>
                      <p className="stat-label">Total Students</p>
                      <div className="stat-meta">
                        <span className="meta-badge info">
                          Learning from you
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="stat-trend-indicator positive">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                    <span>+8%</span>
                  </div>
                </div>

                {/* Revenue Stat Card */}
                <div className="premium-stat-card stat-warning">
                  <div className="stat-card-glow"></div>
                  <div className="stat-card-content">
                    <div className="stat-icon-wrapper">
                      <div className="icon-bg-gradient warning-gradient"></div>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <div className="stat-details">
                      <h3 className="stat-value">{formatCurrency(earnings?.thisMonth || 0)}</h3>
                      <p className="stat-label">This Month</p>
                      <div className="stat-meta">
                        <span className="meta-badge warning">
                          Total Earnings
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="stat-trend-indicator positive">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                    <span>+25%</span>
                  </div>
                </div>

                {/* Rating Stat Card */}
                <div className="premium-stat-card stat-rating">
                  <div className="stat-card-glow"></div>
                  <div className="stat-card-content">
                    <div className="stat-icon-wrapper">
                      <div className="icon-bg-gradient rating-gradient"></div>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                      </svg>
                    </div>
                    <div className="stat-details">
                      <h3 className="stat-value">{dashboardData?.stats?.averageRating || 4.8}</h3>
                      <p className="stat-label">Avg Rating</p>
                      <div className="stat-meta">
                        <span className="meta-badge accent">
                          {dashboardData?.stats?.totalReviews || 142} reviews
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="rating-stars-display">
                    {'⭐'.repeat(5)}
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Bento Grid Layout */}
            <div className="ultra-bento-grid">
              {/* Quick Actions - Enhanced */}
              <div className="ultra-card quick-actions-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Quick Actions</h3>
                    <p>Boost your productivity</p>
                  </div>
                </div>
                <div className="actions-grid-ultra">
                  <button className="action-btn-ultra btn-gradient-blue" onClick={() => setShowCourseModal(true)}>
                    <div className="btn-icon-ultra">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                    </div>
                    <span className="btn-text-ultra">Create Course</span>
                    <div className="btn-glow-ultra"></div>
                  </button>
                  <button className="action-btn-ultra btn-gradient-green" onClick={() => setShowContentModal(true)}>
                    <div className="btn-icon-ultra">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <span className="btn-text-ultra">Upload Content</span>
                    <div className="btn-glow-ultra"></div>
                  </button>
                  <button className="action-btn-ultra btn-gradient-purple" onClick={() => setActiveTab('analytics')}>
                    <div className="btn-icon-ultra">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="20" x2="18" y2="10"/>
                        <line x1="12" y1="20" x2="12" y2="4"/>
                        <line x1="6" y1="20" x2="6" y2="14"/>
                      </svg>
                    </div>
                    <span className="btn-text-ultra">View Analytics</span>
                    <div className="btn-glow-ultra"></div>
                  </button>
                  <button className="action-btn-ultra btn-gradient-gold" onClick={() => setActiveTab('earnings')}>
                    <div className="btn-icon-ultra">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <span className="btn-text-ultra">Earnings</span>
                    <div className="btn-glow-ultra"></div>
                  </button>
                </div>
              </div>

              {/* Performance Overview - Enhanced */}
              <div className="ultra-card performance-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse" style={{background: 'linear-gradient(135deg, #f59e0b, #ef4444)'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Performance Metrics</h3>
                    <p>Real-time insights</p>
                  </div>
                </div>
                <div className="performance-metrics-ultra">
                  <div className="metric-item-ultra">
                    <div className="metric-icon-ultra views-metric">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </div>
                    <div className="metric-content-ultra">
                      <h4>{analytics?.courseViews?.total || 12547}</h4>
                      <p>Total Views</p>
                      <span className="metric-change positive">+18.2%</span>
                    </div>
                  </div>
                  <div className="metric-item-ultra">
                    <div className="metric-icon-ultra completion-metric">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    </div>
                    <div className="metric-content-ultra">
                      <h4>{analytics?.completion?.rate || 87}%</h4>
                      <p>Completion Rate</p>
                      <span className="metric-change positive">+5.8%</span>
                    </div>
                  </div>
                  <div className="metric-item-ultra">
                    <div className="metric-icon-ultra engagement-metric">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                      </svg>
                    </div>
                    <div className="metric-content-ultra">
                      <h4>94%</h4>
                      <p>Engagement</p>
                      <span className="metric-change positive">+12.5%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Courses - Enhanced */}
              <div className="ultra-card top-courses-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse" style={{background: 'linear-gradient(135deg, #fbbf24, #f59e0b)'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Top Performers</h3>
                    <p>Your best courses</p>
                  </div>
                </div>
                <div className="top-courses-list-ultra">
                  {(analytics?.topCourses?.slice(0, 3) || []).map((course, index) => (
                    <div key={index} className="top-course-item-ultra">
                      <div className="course-rank-ultra">
                        <span className={`rank-badge rank-${index + 1}`}>
                          {index === 0 && '🥇'}
                          {index === 1 && '🥈'}
                          {index === 2 && '🥉'}
                        </span>
                      </div>
                      <div className="course-details-ultra">
                        <h4>{course.name}</h4>
                        <div className="course-stats-ultra">
                          <span className="stat-badge-ultra">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                              <circle cx="9" cy="7" r="4"/>
                            </svg>
                            {course.enrollments}
                          </span>
                          <span className="stat-badge-ultra revenue">
                            {formatCurrency(course.revenue)}
                          </span>
                          <span className="stat-badge-ultra rating">
                            ⭐ {course.rating}
                          </span>
                        </div>
                      </div>
                      <div className="course-progress-ultra">
                        <svg width="40" height="40" viewBox="0 0 40 40">
                          <circle cx="20" cy="20" r="16" fill="none" stroke="#e5e7eb" strokeWidth="4"/>
                          <circle cx="20" cy="20" r="16" fill="none" stroke="url(#gradient)" strokeWidth="4" strokeDasharray={`${(course.rating / 5) * 100} 100`} transform="rotate(-90 20 20)"/>
                          <defs>
                            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                              <stop offset="0%" stopColor="#3b82f6"/>
                              <stop offset="100%" stopColor="#8b5cf6"/>
                            </linearGradient>
                          </defs>
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity - Enhanced */}
              <div className="ultra-card activity-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse" style={{background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Recent Activity</h3>
                    <p>Latest updates</p>
                  </div>
                </div>
                <div className="activity-timeline-ultra">
                  {(recentActivity.slice(0, 5).length > 0 ? recentActivity.slice(0, 5) : [
                    {id: 1, message: 'New student enrolled in "Advanced React Patterns"', timestamp: new Date(), type: 'enrollment'},
                    {id: 2, message: 'Course "Node.js Masterclass" received a 5-star review', timestamp: new Date(Date.now() - 3600000), type: 'review'},
                    {id: 3, message: 'Payment received: $156.00', timestamp: new Date(Date.now() - 7200000), type: 'payment'},
                    {id: 4, message: 'Student completed "TypeScript Pro"', timestamp: new Date(Date.now() - 10800000), type: 'completion'}
                  ]).map((activity) => (
                    <div key={activity.id} className="activity-item-ultra">
                      <div className={`activity-indicator-ultra ${activity.type}`}></div>
                      <div className="activity-content-ultra">
                        <p className="activity-message-ultra">{activity.message}</p>
                        <span className="activity-time-ultra">{formatDate(activity.timestamp)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Growth Chart - New Section */}
              <div className="ultra-card growth-chart-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse" style={{background: 'linear-gradient(135deg, #10b981, #059669)'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="20" x2="12" y2="10"/>
                      <line x1="18" y1="20" x2="18" y2="4"/>
                      <line x1="6" y1="20" x2="6" y2="16"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Growth Overview</h3>
                    <p>Last 30 days</p>
                  </div>
                </div>
                <div className="growth-summary-ultra">
                  <div className="growth-item-ultra">
                    <div className="growth-label-ultra">Student Growth</div>
                    <div className="growth-value-ultra">+342</div>
                    <div className="growth-bar-ultra">
                      <div className="growth-fill-ultra" style={{width: '85%', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)'}}></div>
                    </div>
                  </div>
                  <div className="growth-item-ultra">
                    <div className="growth-label-ultra">Revenue Growth</div>
                    <div className="growth-value-ultra">+$4,250</div>
                    <div className="growth-bar-ultra">
                      <div className="growth-fill-ultra" style={{width: '92%', background: 'linear-gradient(90deg, #10b981, #14b8a6)'}}></div>
                    </div>
                  </div>
                  <div className="growth-item-ultra">
                    <div className="growth-label-ultra">Course Completions</div>
                    <div className="growth-value-ultra">+156</div>
                    <div className="growth-bar-ultra">
                      <div className="growth-fill-ultra" style={{width: '78%', background: 'linear-gradient(90deg, #f59e0b, #fbbf24)'}}></div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notifications - Enhanced */}
              <div className="ultra-card notifications-ultra">
                <div className="card-header-ultra">
                  <div className="header-icon-pulse" style={{background: 'linear-gradient(135deg, #ec4899, #f43f5e)'}}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                      <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                    </svg>
                  </div>
                  <div className="header-text-ultra">
                    <h3>Notifications</h3>
                    <p>Stay informed</p>
                  </div>
                </div>
                <div className="notifications-list-ultra">
                  {(notifications.slice(0, 4).length > 0 ? notifications.slice(0, 4) : [
                    {id: 1, title: 'New Message', message: 'You have 3 new messages from students', read: false, type: 'message'},
                    {id: 2, title: 'Course Update', message: 'Your course update was published', read: false, type: 'success'},
                    {id: 3, title: 'Review Received', message: 'Someone left a review on your course', read: true, type: 'info'}
                  ]).map((notification) => (
                    <div key={notification.id} className={`notification-item-ultra ${notification.read ? 'read' : 'unread'}`}>
                      <div className={`notification-dot-ultra ${notification.type}`}></div>
                      <div className="notification-text-ultra">
                        <h5>{notification.title}</h5>
                        <p>{notification.message}</p>
                      </div>
                      {!notification.read && <div className="unread-badge-ultra"></div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'courses' && (
          <div className="courses-container-creator">
            <div className="section-header-creator">
              <div className="header-title-group">
                <svg className="header-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
                <h3>My Courses ({myCourses.length})</h3>
              </div>
              <div className="header-actions-creator">
                <select className="filter-select-modern">
                  <option value="all">All Courses</option>
                  <option value="published">Published</option>
                  <option value="draft">Draft</option>
                  <option value="pending">Pending Review</option>
                  <option value="rejected">Rejected</option>
                </select>
                <button className="action-btn-creator primary" onClick={() => setShowCourseModal(true)}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Create New Course
                </button>
              </div>
            </div>
            
            {myCourses.length > 0 ? (
              <div className="courses-table-wrapper">
                <table className="data-table-creator">
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Status</th>
                      <th>Students</th>
                      <th>Rating</th>
                      <th>Price</th>
                      <th>Duration</th>
                      <th>Lessons</th>
                      <th>Revenue</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myCourses.map((course) => (
                      <tr key={course._id}>
                        <td className="course-cell">
                          <div className="course-thumbnail-icon">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                              <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                            </svg>
                          </div>
                          <div className="course-info">
                            <div className="course-title">{course.title}</div>
                            <div className="course-meta">
                              Created: {new Date(course.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </td>
                        
                        <td>
                          <span className={`status-badge-creator ${course.status}`}>
                            {course.status === 'published' && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                              </svg>
                            )}
                            {course.status === 'draft' && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            )}
                            {course.status === 'pending-approval' && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <polyline points="12 6 12 12 16 14"></polyline>
                              </svg>
                            )}
                            {course.status === 'rejected' && (
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <line x1="15" y1="9" x2="9" y2="15"></line>
                                <line x1="9" y1="9" x2="15" y2="15"></line>
                              </svg>
                            )}
                            {course.status.replace('-', ' ')}
                          </span>
                          {course.status === 'rejected' && course.rejectionReason && (
                            <div className="rejection-note" title={course.rejectionReason}>
                              {course.rejectionReason.substring(0, 30)}...
                            </div>
                          )}
                        </td>
                        
                        <td className="metric-cell">
                          <div className="metric-value">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                              <circle cx="9" cy="7" r="4"/>
                              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                            </svg>
                            {course.enrollmentCount || 0}
                          </div>
                        </td>
                        
                        <td className="metric-cell">
                          <div className="metric-value rating">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
                            </svg>
                            {course.averageRating || '0.0'}
                          </div>
                        </td>
                        
                        <td className="price-cell">
                          {formatCurrency(course.pricing?.price || course.price || 0)}
                        </td>
                        
                        <td className="metric-cell">
                          <div className="metric-value">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <polyline points="12 6 12 12 16 14"></polyline>
                            </svg>
                            {course.totalDuration ? `${(course.totalDuration / 60).toFixed(1)}h` : '0h'}
                          </div>
                        </td>
                        
                        <td className="metric-cell">
                          <div className="metric-value">
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                            </svg>
                            {course.totalLessons || course.lessonCount || 0}
                          </div>
                        </td>
                        
                        <td className="revenue-cell">
                          {formatCurrency(course.revenue || 0)}
                        </td>
                        
                        <td>
                          <div className="action-buttons-compact">
                            <button 
                              className="icon-btn-creator"
                              onClick={() => handleCourseAction(course._id, 'edit')}
                              disabled={course.status === 'pending-approval'}
                              title="Edit Course"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                            </button>
                            
                            {course.status === 'draft' && (
                              <button 
                                className="icon-btn-creator success"
                                onClick={() => handleCourseAction(course._id, 'submit-for-approval')}
                                title="Submit for Approval"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <line x1="22" y1="2" x2="11" y2="13"></line>
                                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                                </svg>
                              </button>
                            )}
                            
                            {course.status === 'published' && (
                              <button 
                                className="icon-btn-creator warning"
                                onClick={() => handleCourseAction(course._id, 'unpublish')}
                                title="Unpublish Course"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="3 6 5 6 21 6"></polyline>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                  <line x1="10" y1="11" x2="10" y2="17"></line>
                                  <line x1="14" y1="11" x2="14" y2="17"></line>
                                </svg>
                              </button>
                            )}
                            
                            {course.status === 'rejected' && (
                              <button 
                                className="icon-btn-creator success"
                                onClick={() => handleCourseAction(course._id, 'submit-for-approval')}
                                title="Resubmit for Approval"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="23 4 23 10 17 10"></polyline>
                                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                                </svg>
                              </button>
                            )}
                            
                            <button 
                              className="icon-btn-creator info"
                              onClick={() => handleCourseAction(course._id, 'analytics')}
                              title="View Analytics"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="20" x2="18" y2="10"></line>
                                <line x1="12" y1="20" x2="12" y2="4"></line>
                                <line x1="6" y1="20" x2="6" y2="14"></line>
                              </svg>
                            </button>
                            
                            <button 
                              className="icon-btn-creator danger"
                              onClick={() => handleCourseAction(course._id, 'delete')}
                              disabled={course.status === 'pending-approval'}
                              title="Delete Course"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6"></polyline>
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state-creator">
                <div className="empty-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                </div>
                <h3>No Courses Yet</h3>
                <p>Create your first course to start teaching and earning</p>
                <button onClick={() => setShowCourseModal(true)} className="action-btn-creator primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Create Your First Course
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'students' && (
          <div className="students-section">
            <div className="section-header">
              <h3>👥 My Students</h3>
              <button className="export-btn">📊 Export Data</button>
            </div>
            
            <div className="students-table">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Enrolled Courses</th>
                    <th>Progress</th>
                    <th>Last Active</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((student) => (
                    <tr key={student._id}>
                      <td>{student.name}</td>
                      <td>{student.email}</td>
                      <td>
                        <span className="course-count">
                          {Math.floor(Math.random() * 3) + 1} courses
                        </span>
                      </td>
                      <td>
                        <div className="progress-bar">
                          <div 
                            className="progress-fill" 
                            style={{width: `${Math.floor(Math.random() * 100)}%`}}
                          ></div>
                          <span>{Math.floor(Math.random() * 100)}%</span>
                        </div>
                      </td>
                      <td>{new Date(student.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div className="action-buttons">
                          <button className="message-btn" title="Message">💬</button>
                          <button className="view-btn" title="View Profile">👁️</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Course Enrollments Section */}
            <div className="section-header" style={{ marginTop: '2rem' }}>
              <h3>📚 Course Enrollments</h3>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <button 
                  className="export-btn"
                  onClick={() => loadDemoEnrollments()}
                >
                  🔄 Refresh
                </button>
                <span style={{ fontSize: '0.9rem', opacity: 0.7 }}>
                  {enrollments.length} enrollments
                </span>
              </div>
            </div>

            {enrollments.length === 0 ? (
              <div className="empty-state-creator" style={{ marginTop: '1rem' }}>
                <div className="empty-icon-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                </div>
                <h3>No Enrollments Yet</h3>
                <p>Student enrollments for your courses will appear here</p>
              </div>
            ) : (
              <div className="students-table" style={{ marginTop: '1rem' }}>
                <table>
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Course</th>
                      <th>Enrollment Code</th>
                      <th>Enrolled Date</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Progress</th>
                      <th>Lab Access</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((enrollment, index) => (
                      <tr key={enrollment._id || index}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '14px'
                            }}>
                              {typeof enrollment.student === 'object' 
                                ? (enrollment.student?.name?.charAt(0) || 'S')
                                : 'S'}
                            </div>
                            <div>
                              <div style={{ fontWeight: '500' }}>
                                {typeof enrollment.student === 'object' 
                                  ? (enrollment.student?.name || 'Student')
                                  : `Student ${enrollment.student || 'Unknown'}`}
                              </div>
                              <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                                {typeof enrollment.student === 'object' 
                                  ? (enrollment.student?.email || 'N/A')
                                  : 'N/A'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div style={{ fontWeight: '500' }}>
                              {enrollment.course?.title || 'Unknown Course'}
                            </div>
                            <div style={{ fontSize: '0.85rem', opacity: 0.6 }}>
                              by {enrollment.course?.instructor?.name || 'Unknown'}
                            </div>
                          </div>
                        </td>
                        <td>
                          <code style={{
                            padding: '4px 8px',
                            borderRadius: '4px',
                            background: 'rgba(59, 130, 246, 0.1)',
                            color: '#3b82f6',
                            fontFamily: 'monospace',
                            fontSize: '0.85rem'
                          }}>
                            {enrollment.enrollmentCode || 'N/A'}
                          </code>
                        </td>
                        <td>
                          {new Date(enrollment.enrollmentDate || enrollment.createdAt).toLocaleDateString()}
                        </td>
                        <td>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.85rem',
                              fontWeight: '500',
                              background: enrollment.paymentStatus === 'completed' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                              color: enrollment.paymentStatus === 'completed' ? '#10b981' : '#6b7280'
                            }}>
                              {enrollment.paymentStatus === 'completed' ? '✓ Paid' : '💵 Free'}
                            </span>
                            {enrollment.paymentAmount && (
                              <span style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                                {enrollment.paymentAmount}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '0.85rem',
                            fontWeight: '500',
                            background: 'rgba(34, 197, 94, 0.1)',
                            color: '#22c55e'
                          }}>
                            {enrollment.status || 'active'}
                          </span>
                        </td>
                        <td>
                          <div style={{ width: '100%' }}>
                            <div style={{
                              width: '100%',
                              height: '8px',
                              background: 'rgba(0,0,0,0.1)',
                              borderRadius: '4px',
                              overflow: 'hidden'
                            }}>
                              <div style={{
                                width: `${enrollment.progress?.percentageComplete || 0}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                                borderRadius: '4px',
                                transition: 'width 0.3s ease'
                              }}></div>
                            </div>
                            <div style={{ fontSize: '0.75rem', marginTop: '4px', opacity: 0.7 }}>
                              {enrollment.progress?.percentageComplete || 0}%
                            </div>
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '1.2rem' }}>🧪</span>
                            <span style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                              {enrollment.labAccess?.length || 0} labs
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="creative-analytics">
            {/* Analytics Hero Banner */}
            <div className="analytics-hero-banner">
              <div className="floating-orb orb-analytics-1"></div>
              <div className="floating-orb orb-analytics-2"></div>
              
              <div className="analytics-hero-content">
                <div className="analytics-welcome">
                  <h1 className="gradient-text-analytics">Performance Insights 📊</h1>
                  <p className="analytics-tagline">Track your teaching success in real-time</p>
                </div>
                
                <div className="analytics-mega-stats">
                  <div className="analytics-mega-card views-card">
                    <div className="analytics-icon-mega">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </div>
                    <div className="analytics-content-mega">
                      <span className="analytics-label">Total Views</span>
                      <span className="analytics-value">{analytics?.courseViews?.total || 12845}</span>
                      <span className="analytics-trend positive">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                          <polyline points="17 6 23 6 23 12"/>
                        </svg>
                        +{analytics?.courseViews?.growth || 15.3}% this week
                      </span>
                    </div>
                  </div>

                  <div className="analytics-mega-card">
                    <div className="analytics-icon-mega teal-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                      </svg>
                    </div>
                    <div className="analytics-mega-info">
                      <p className="analytics-mega-label">Engagement Rate</p>
                      <h3 className="analytics-mega-value">87.5%</h3>
                      <span className="analytics-mega-change positive">+5.2% improvement</span>
                    </div>
                  </div>

                  <div className="analytics-mega-card">
                    <div className="analytics-icon-mega orange-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                      </svg>
                    </div>
                    <div className="analytics-mega-info">
                      <p className="analytics-mega-label">Completion Rate</p>
                      <h3 className="analytics-mega-value">78.5%</h3>
                      <span className="analytics-mega-change positive">Above 65% industry avg</span>
                    </div>
                  </div>

                  <div className="analytics-mega-card">
                    <div className="analytics-icon-mega gold-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <div className="analytics-mega-info">
                      <p className="analytics-mega-label">Revenue/Student</p>
                      <h3 className="analytics-mega-value">$45.80</h3>
                      <span className="analytics-mega-change positive">+12.5% growth</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Analytics Bento Grid */}
            <div className="analytics-bento-grid">
              {/* Top Courses Performance */}
              <div className="analytics-card-creative wide-card">
                <div className="analytics-card-header">
                  <h4>🎯 Top Performing Courses</h4>
                  <span className="view-all-link">View All →</span>
                </div>
                <div className="top-courses-list">
                  {(analytics?.topCourses || []).length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
                      No course performance data yet — data will appear once students enroll.
                    </div>
                  ) : (analytics?.topCourses || []).map((course, idx) => (
                    <div key={idx} className="course-performance-row">
                      <div className="course-rank">#{idx + 1}</div>
                      <div className="course-info">
                        <span className="course-name">{course.name}</span>
                        <div className="course-metrics">
                          <span>👥 {course.students} students</span>
                          <span>⭐ {course.rating}</span>
                          <span>💰 {course.revenue}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Engagement */}
              <div className="analytics-card-creative">
                <div className="analytics-card-header">
                  <h4>📈 Engagement Trends</h4>
                </div>
                <div className="engagement-stats">
                  <div className="stat-row">
                    <span className="stat-label">Average Watch Time</span>
                    <span className="stat-value">42 min</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Quiz Completion</span>
                    <span className="stat-value">85%</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Assignment Submission</span>
                    <span className="stat-value">78%</span>
                  </div>
                  <div className="stat-row">
                    <span className="stat-label">Discussion Participation</span>
                    <span className="stat-value">62%</span>
                  </div>
                </div>
              </div>

              {/* Traffic Sources */}
              <div className="analytics-card-creative">
                <div className="analytics-card-header">
                  <h4>🌐 Traffic Sources</h4>
                </div>
                <div className="traffic-sources">
                  {[
                    { source: 'Direct', percent: 45, color: '#b794f6' },
                    { source: 'Search', percent: 30, color: '#60a5fa' },
                    { source: 'Social Media', percent: 15, color: '#f472b6' },
                    { source: 'Referral', percent: 10, color: '#fbbf24' }
                  ].map((item, idx) => (
                    <div key={idx} className="traffic-source-row">
                      <div className="source-info">
                        <span className="source-name">{item.source}</span>
                        <span className="source-percent">{item.percent}%</span>
                      </div>
                      <div className="source-bar">
                        <div 
                          className="source-fill" 
                          style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Demographics */}
              <div className="analytics-card-creative">
                <div className="analytics-card-header">
                  <h4>🌍 Student Demographics</h4>
                </div>
                <div className="demographics-list">
                  {[
                    { country: '🇺🇸 United States', percent: 35 },
                    { country: '🇬🇧 United Kingdom', percent: 20 },
                    { country: '🇨🇦 Canada', percent: 15 },
                    { country: '🇦🇺 Australia', percent: 12 },
                    { country: '🌎 Others', percent: 18 }
                  ].map((demo, idx) => (
                    <div key={idx} className="demo-row">
                      <span className="demo-country">{demo.country}</span>
                      <span className="demo-percent">{demo.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue Breakdown */}
              <div className="analytics-card-creative wide-card">
                <div className="analytics-card-header">
                  <h4>💰 Revenue by Course Category</h4>
                </div>
                <div className="revenue-breakdown">
                  {[
                    { category: 'AI & Sustainability', revenue: 8500, color: '#b794f6' },
                    { category: 'Renewable Energy', revenue: 6200, color: '#60a5fa' },
                    { category: 'Circular Economy', revenue: 4800, color: '#10b981' },
                    { category: 'Climate Tech', revenue: 3100, color: '#f472b6' },
                    { category: 'Smart Cities', revenue: 1980, color: '#fbbf24' }
                  ].map((item, idx) => (
                    <div key={idx} className="revenue-category-row">
                      <div className="category-color" style={{ backgroundColor: item.color }}></div>
                      <div className="category-info">
                        <span className="category-name">{item.category}</span>
                        <span className="category-revenue">${item.revenue.toLocaleString()}</span>
                      </div>
                      <div className="category-bar">
                        <div 
                          className="category-fill" 
                          style={{ 
                            width: `${(item.revenue / 8500) * 100}%`, 
                            backgroundColor: item.color 
                          }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div className="analytics-card-creative tall-card">
                <div className="analytics-card-header">
                  <h4>⚡ Recent Activity</h4>
                </div>
                <div className="activity-feed">
                  {[
                    { icon: '👤', text: 'New enrollment in "AI for Sustainable Manufacturing"', time: '5 min ago' },
                    { icon: '⭐', text: 'New 5-star review on "Renewable Energy Systems"', time: '12 min ago' },
                    { icon: '🎓', text: '15 students completed "Circular Economy" course', time: '1 hour ago' },
                    { icon: '💬', text: 'New question in course discussion', time: '2 hours ago' },
                    { icon: '📊', text: 'Course views reached 10K milestone', time: '3 hours ago' }
                  ].map((activity, idx) => (
                    <div key={idx} className="activity-item">
                      <span className="activity-icon">{activity.icon}</span>
                      <div className="activity-content">
                        <p className="activity-text">{activity.text}</p>
                        <span className="activity-time">{activity.time}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Student Satisfaction */}
              <div className="analytics-card-creative">
                <div className="analytics-card-header">
                  <h4>😊 Student Satisfaction</h4>
                </div>
                <div className="satisfaction-stats">
                  <div className="satisfaction-score">
                    <div className="score-circle">
                      <span className="score-number">4.7</span>
                      <span className="score-max">/5.0</span>
                    </div>
                    <span className="score-label">Average Rating</span>
                  </div>
                  <div className="rating-breakdown">
                    {[5, 4, 3, 2, 1].map((star) => (
                      <div key={star} className="rating-row">
                        <span className="stars">{'⭐'.repeat(star)}</span>
                        <div className="rating-bar">
                          <div 
                            className="rating-fill" 
                            style={{ width: `${star === 5 ? 65 : star === 4 ? 25 : star === 3 ? 7 : 2}%` }}
                          ></div>
                        </div>
                        <span className="rating-count">{star === 5 ? 65 : star === 4 ? 25 : star === 3 ? 7 : 2}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'earnings' && (
          <div className="creative-earnings">
            {/* Earnings Hero Banner */}
            <div className="earnings-hero-banner">
              <div className="orb-earnings-1"></div>
              <div className="orb-earnings-2"></div>
              <div className="orb-earnings-3"></div>
              
              <div className="earnings-hero-content">
                <div className="earnings-welcome">
                  <h2 className="gradient-text-earnings">Revenue Dashboard 💰</h2>
                  <p className="earnings-subtitle">Track your income and financial growth</p>
                </div>

                {/* Earnings Mega Stats */}
                <div className="earnings-mega-stats">
                  <div className="earnings-mega-card">
                    <div className="earnings-icon-mega purple-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                      </svg>
                    </div>
                    <div className="earnings-mega-info">
                      <p className="earnings-mega-label">Total Earnings</p>
                      <h3 className="earnings-mega-value">${earnings?.total || '24,580'}</h3>
                      <span className="earnings-mega-change positive">+18.5% this year</span>
                    </div>
                  </div>

                  <div className="earnings-mega-card">
                    <div className="earnings-icon-mega teal-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="2" y="5" width="20" height="14" rx="2"/>
                        <line x1="2" y1="10" x2="22" y2="10"/>
                      </svg>
                    </div>
                    <div className="earnings-mega-info">
                      <p className="earnings-mega-label">This Month</p>
                      <h3 className="earnings-mega-value">${earnings?.thisMonth || '3,240'}</h3>
                      <span className="earnings-mega-change positive">+25.3% vs last month</span>
                    </div>
                  </div>

                  <div className="earnings-mega-card">
                    <div className="earnings-icon-mega orange-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <polyline points="12 6 12 12 16 14"/>
                      </svg>
                    </div>
                    <div className="earnings-mega-info">
                      <p className="earnings-mega-label">Pending Payout</p>
                      <h3 className="earnings-mega-value">${earnings?.pending || '1,850'}</h3>
                      <span className="earnings-mega-change neutral">Next: Dec 1, 2025</span>
                    </div>
                  </div>

                  <div className="earnings-mega-card">
                    <div className="earnings-icon-mega gold-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <polyline points="19 12 12 19 5 12"/>
                      </svg>
                    </div>
                    <div className="earnings-mega-info">
                      <p className="earnings-mega-label">Avg. per Student</p>
                      <h3 className="earnings-mega-value">$45.80</h3>
                      <span className="earnings-mega-change positive">+12.5% growth</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Earnings Bento Grid */}
            <div className="earnings-bento-grid">
              {/* Monthly Revenue Breakdown */}
              <div className="earnings-card-creative wide-card">
                <div className="earnings-card-header">
                  <h4>💵 Monthly Revenue Breakdown</h4>
                  <span className="view-all-link">View All →</span>
                </div>
                <div className="monthly-revenue-list">
                  {[
                    { month: 'November 2025', revenue: 3240, courses: 78, growth: 25.3 },
                    { month: 'October 2025', revenue: 2589, courses: 62, growth: 18.2 },
                    { month: 'September 2025', revenue: 2190, courses: 54, growth: 12.5 },
                    { month: 'August 2025', revenue: 1947, courses: 48, growth: 8.9 }
                  ].map((item, idx) => (
                    <div key={idx} className="revenue-month-row">
                      <div className="month-info">
                        <span className="month-name">{item.month}</span>
                        <span className="month-courses">{item.courses} sales</span>
                      </div>
                      <div className="month-stats">
                        <span className="month-revenue">${item.revenue.toLocaleString()}</span>
                        <span className="month-growth positive">+{item.growth}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Earning Courses */}
              <div className="earnings-card-creative">
                <div className="earnings-card-header">
                  <h4>🏆 Top Earning Courses</h4>
                </div>
                <div className="top-earning-courses">
                  {[
                    { name: 'AI for Sustainable Manufacturing', revenue: 8520, students: 142 },
                    { name: 'Renewable Energy Systems', revenue: 6890, students: 115 },
                    { name: 'Circular Economy Principles', revenue: 5240, students: 87 }
                  ].map((course, idx) => (
                    <div key={idx} className="earning-course-row">
                      <div className="course-rank-badge">#{idx + 1}</div>
                      <div className="course-earning-info">
                        <span className="course-title-small">{course.name}</span>
                        <div className="course-earning-stats">
                          <span>💰 ${course.revenue.toLocaleString()}</span>
                          <span>👥 {course.students}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payment Methods */}
              <div className="earnings-card-creative">
                <div className="earnings-card-header">
                  <h4>💳 Payment Methods</h4>
                </div>
                <div className="payment-methods-list">
                  {[
                    { type: 'Credit Card', percent: 65, icon: '💳' },
                    { type: 'PayPal', percent: 25, icon: '🅿️' },
                    { type: 'Bank Transfer', percent: 10, icon: '🏦' }
                  ].map((method, idx) => (
                    <div key={idx} className="payment-method-row">
                      <div className="method-info">
                        <span className="method-icon">{method.icon}</span>
                        <span className="method-name">{method.type}</span>
                      </div>
                      <span className="method-percent">{method.percent}%</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Revenue by Category */}
              <div className="earnings-card-creative wide-card">
                <div className="earnings-card-header">
                  <h4>📊 Revenue by Category</h4>
                </div>
                <div className="revenue-by-category">
                  {[
                    { category: 'AI & Sustainability', revenue: 8500, color: '#b794f6', percent: 35 },
                    { category: 'Renewable Energy', revenue: 6200, color: '#60a5fa', percent: 25 },
                    { category: 'Circular Economy', revenue: 4800, color: '#10b981', percent: 20 },
                    { category: 'Climate Tech', revenue: 3100, color: '#f472b6', percent: 13 },
                    { category: 'Others', revenue: 1980, color: '#fbbf24', percent: 7 }
                  ].map((item, idx) => (
                    <div key={idx} className="category-revenue-row">
                      <div className="category-badge" style={{ backgroundColor: item.color }}></div>
                      <div className="category-details">
                        <span className="category-label">{item.category}</span>
                        <div className="category-revenue-bar">
                          <div 
                            className="category-revenue-fill" 
                            style={{ width: `${item.percent}%`, backgroundColor: item.color }}
                          ></div>
                        </div>
                      </div>
                      <div className="category-amount">
                        <span className="amount-value">${item.revenue.toLocaleString()}</span>
                        <span className="amount-percent">{item.percent}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Payout History */}
              <div className="earnings-card-creative tall-card">
                <div className="earnings-card-header">
                  <h4>� Payout History</h4>
                </div>
                <div className="payout-history">
                  {[
                    { date: 'Nov 1, 2025', amount: 2589, status: 'completed', method: 'Bank Transfer' },
                    { date: 'Oct 1, 2025', amount: 2190, status: 'completed', method: 'PayPal' },
                    { date: 'Sep 1, 2025', amount: 1947, status: 'completed', method: 'Bank Transfer' },
                    { date: 'Dec 1, 2025', amount: 3240, status: 'pending', method: 'Bank Transfer' }
                  ].map((payout, idx) => (
                    <div key={idx} className="payout-item">
                      <div className="payout-icon">
                        {payout.status === 'completed' ? '✅' : '⏳'}
                      </div>
                      <div className="payout-details">
                        <span className="payout-date">{payout.date}</span>
                        <span className="payout-method">{payout.method}</span>
                      </div>
                      <div className="payout-amount-section">
                        <span className="payout-amount">${payout.amount.toLocaleString()}</span>
                        <span className={`payout-status ${payout.status}`}>
                          {payout.status === 'completed' ? 'Paid' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Earnings Growth */}
              <div className="earnings-card-creative">
                <div className="earnings-card-header">
                  <h4>📈 Growth Metrics</h4>
                </div>
                <div className="growth-metrics">
                  <div className="growth-metric-item">
                    <span className="metric-label">Year over Year</span>
                    <span className="metric-value positive">+145%</span>
                  </div>
                  <div className="growth-metric-item">
                    <span className="metric-label">Quarter over Quarter</span>
                    <span className="metric-value positive">+38%</span>
                  </div>
                  <div className="growth-metric-item">
                    <span className="metric-label">Best Month</span>
                    <span className="metric-value">Nov 2025</span>
                  </div>
                  <div className="growth-metric-item">
                    <span className="metric-label">Projected Year End</span>
                    <span className="metric-value">$42,500</span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="earnings-card-creative">
                <div className="earnings-card-header">
                  <h4>⚡ Quick Actions</h4>
                </div>
                <div className="quick-actions-earnings">
                  <button className="action-button-earnings primary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="1" x2="12" y2="23"/>
                      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                    <span>Request Payout</span>
                  </button>
                  <button className="action-button-earnings secondary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                    <span>Download Invoice</span>
                  </button>
                  <button className="action-button-earnings secondary">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                    <span>View Tax Documents</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className="creative-content-library">
            {/* Content Hero Banner */}
            <div className="content-hero-banner">
              <div className="orb-content-1"></div>
              <div className="orb-content-2"></div>
              <div className="orb-content-3"></div>
              
              <div className="content-hero-content">
                <div className="content-welcome">
                  <h2 className="gradient-text-content">Content Library 📚</h2>
                  <p className="content-subtitle">Manage your course materials and resources</p>
                </div>

                {/* Content Mega Stats */}
                <div className="content-mega-stats">
                  <div className="content-mega-card">
                    <div className="content-icon-mega purple-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/>
                        <polyline points="13 2 13 9 20 9"/>
                      </svg>
                    </div>
                    <div className="content-mega-info">
                      <p className="content-mega-label">Total Files</p>
                      <h3 className="content-mega-value">{contentLibrary.length || 24}</h3>
                      <span className="content-mega-change neutral">All types</span>
                    </div>
                  </div>

                  <div className="content-mega-card">
                    <div className="content-icon-mega teal-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polygon points="23 7 16 12 23 17 23 7"/>
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                      </svg>
                    </div>
                    <div className="content-mega-info">
                      <p className="content-mega-label">Videos</p>
                      <h3 className="content-mega-value">{contentLibrary.filter(c => c.type === 'video').length || 12}</h3>
                      <span className="content-mega-change positive">85 GB total</span>
                    </div>
                  </div>

                  <div className="content-mega-card">
                    <div className="content-icon-mega orange-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <polyline points="10 9 9 9 8 9"/>
                      </svg>
                    </div>
                    <div className="content-mega-info">
                      <p className="content-mega-label">Documents</p>
                      <h3 className="content-mega-value">{contentLibrary.filter(c => c.type === 'pdf').length || 8}</h3>
                      <span className="content-mega-change positive">45 MB total</span>
                    </div>
                  </div>

                  <div className="content-mega-card">
                    <div className="content-icon-mega gold-glow">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <div className="content-mega-info">
                      <p className="content-mega-label">Storage Used</p>
                      <h3 className="content-mega-value">146 MB</h3>
                      <span className="content-mega-change neutral">of 10 GB</span>
                    </div>
                  </div>
                </div>

                {/* Upload & Filter Actions */}
                <div className="content-actions-bar">
                  <div className="filter-tabs">
                    <button className="filter-tab active">All Content</button>
                    <button className="filter-tab">Videos 📹</button>
                    <button className="filter-tab">Documents 📄</button>
                    <button className="filter-tab">Archives 📦</button>
                  </div>
                  <button className="upload-btn-creative" onClick={() => setShowContentModal(true)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span>Upload Content</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Content Bento Grid */}
            <div className="content-bento-grid">
              {contentLibrary.length > 0 ? (
                contentLibrary.map((content) => (
                  <div key={content.id} className="content-card-creative">
                    <div className="content-card-visual">
                      <div className={`file-type-badge ${content.type}`}>
                        {content.type === 'video' ? '📹' : 
                         content.type === 'pdf' ? '📄' : 
                         content.type === 'zip' ? '📦' : '📁'}
                      </div>
                      <div className={`status-badge-creative ${getStatusBadge(content.status)}`}>
                        {content.status}
                      </div>
                    </div>
                    
                    <div className="content-card-body">
                      <h5 className="content-title">{content.name}</h5>
                      
                      <div className="content-meta">
                        {content.type === 'video' && (
                          <>
                            <span className="meta-badge duration">⏱ {content.duration}</span>
                            <span className="meta-badge size">💾 {content.size}</span>
                          </>
                        )}
                        {content.type === 'pdf' && (
                          <>
                            <span className="meta-badge pages">📄 {content.pages} pages</span>
                            <span className="meta-badge size">💾 {content.size}</span>
                          </>
                        )}
                        {content.type === 'zip' && (
                          <>
                            <span className="meta-badge files">📦 {content.files} files</span>
                            <span className="meta-badge size">💾 {content.size}</span>
                          </>
                        )}
                      </div>

                      <div className="content-course-badge">
                        <span className="course-label">Course:</span>
                        <span className="course-name">{content.course}</span>
                      </div>

                      <p className="upload-info">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {new Date(content.uploadDate).toLocaleDateString()}
                      </p>
                    </div>

                    <div className="content-card-actions">
                      <button 
                        className="action-btn-creative edit"
                        onClick={() => handleContentAction(content.id, 'edit')}
                        title="Edit"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button 
                        className="action-btn-creative download"
                        onClick={() => handleContentAction(content.id, 'download')}
                        title="Download"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </button>
                      <button 
                        className="action-btn-creative delete"
                        onClick={() => handleContentAction(content.id, 'delete')}
                        title="Delete"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-creative">
                  <div className="empty-orb"></div>
                  <div className="empty-icon-creative">📚</div>
                  <h3 className="empty-title">No Content Yet</h3>
                  <p className="empty-description">Upload your first content to start building amazing courses</p>
                  <button onClick={() => setShowContentModal(true)} className="empty-action-btn">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    Upload First Content
                  </button>
                </div>
              )}
            </div>

            {/* Storage & Quick Stats */}
            <div className="content-bottom-stats">
              <div className="storage-card">
                <div className="storage-header">
                  <div className="storage-icon-wrapper">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <ellipse cx="12" cy="5" rx="9" ry="3"/>
                      <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
                    </svg>
                  </div>
                  <div>
                    <h4>Storage Usage</h4>
                    <p>146 MB of 10 GB used</p>
                  </div>
                </div>
                <div className="storage-bar">
                  <div className="storage-fill" style={{width: '1.46%'}}></div>
                </div>
                <div className="storage-details">
                  <span>1.46% used</span>
                  <span>9.85 GB available</span>
                </div>
              </div>

              <div className="quick-stats-cards">
                <div className="quick-stat-card">
                  <div className="quick-stat-icon video-color">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="23 7 16 12 23 17 23 7"/>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                    </svg>
                  </div>
                  <div className="quick-stat-info">
                    <p className="quick-stat-value">{contentLibrary.filter(c => c.type === 'video').length || 12}</p>
                    <p className="quick-stat-label">Videos</p>
                  </div>
                </div>
                <div className="quick-stat-card">
                  <div className="quick-stat-icon doc-color">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                  <div className="quick-stat-info">
                    <p className="quick-stat-value">{contentLibrary.filter(c => c.type === 'pdf').length || 8}</p>
                    <p className="quick-stat-label">Documents</p>
                  </div>
                </div>
                <div className="quick-stat-card">
                  <div className="quick-stat-icon archive-color">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                    </svg>
                  </div>
                  <div className="quick-stat-info">
                    <p className="quick-stat-value">{contentLibrary.filter(c => c.type === 'zip').length || 4}</p>
                    <p className="quick-stat-label">Archives</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-section">
            <h3>⚙️ Account Settings</h3>
            <div className="settings-content">
              <div className="settings-card">
                <h4>Profile Information</h4>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Display Name</label>
                    <input type="text" defaultValue={user?.name || ''} className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" defaultValue={user?.email || ''} className="form-input" disabled />
                  </div>
                  <div className="form-group">
                    <label>Bio</label>
                    <textarea className="form-input" rows="4" placeholder="Tell students about yourself..."></textarea>
                  </div>
                  <button className="action-btn-creator primary">Save Changes</button>
                </div>
              </div>

              <div className="settings-card">
                <h4>Notification Preferences</h4>
                <div className="settings-form">
                  <label className="checkbox-label">
                    <input type="checkbox" defaultChecked /> Email notifications for new enrollments
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" defaultChecked /> Email notifications for course reviews
                  </label>
                  <label className="checkbox-label">
                    <input type="checkbox" /> Email notifications for platform updates
                  </label>
                  <button className="action-btn-creator primary">Save Preferences</button>
                </div>
              </div>

              <div className="settings-card">
                <h4>Appearance</h4>
                <div className="settings-form">
                  <div className="theme-switch-row">
                    <div>
                      <p className="theme-switch-title">{isLightMode ? 'Light mode enabled' : 'Dark mode enabled'}</p>
                      <p className="theme-switch-subtitle">Switch between dark and white UI while keeping your preferences.</p>
                    </div>
                    <button
                      type="button"
                      onClick={toggleThemeMode}
                      className={`theme-switch ${isLightMode ? 'is-light' : ''}`}
                      aria-label="Toggle dark and white mode"
                    >
                      <span className="theme-switch-knob"></span>
                    </button>
                  </div>
                </div>
              </div>

              <div className="settings-card">
                <h4>Change Password</h4>
                <div className="settings-form">
                  <div className="form-group">
                    <label>Current Password</label>
                    <input type="password" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>New Password</label>
                    <input type="password" className="form-input" />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input type="password" className="form-input" />
                  </div>
                  <button className="action-btn-creator primary">Update Password</button>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* ============================================================
          AI CREATOR STUDIO — 5 NEW TABS
          ============================================================ */}

      {/* === AI STUDIO TAB === */}
      {activeTab === 'ai-studio' && (
        <AIStudioTab API_BASE={process.env.REACT_APP_API_BASE || ''} token={localStorage.getItem('authToken') || localStorage.getItem('token')} />
      )}

      {/* === SLIDE BUILDER TAB === */}
      {activeTab === 'slide-builder' && (
        <SlideBuilderTab API_BASE={process.env.REACT_APP_API_BASE || ''} token={localStorage.getItem('authToken') || localStorage.getItem('token')} />
      )}

      {/* === VIDEO LAB TAB === */}
      {activeTab === 'video-lab' && (
        <VideoLabTab API_BASE={process.env.REACT_APP_API_BASE || ''} token={localStorage.getItem('authToken') || localStorage.getItem('token')} />
      )}

      {/* === MENTORING HUB TAB === */}
      {activeTab === 'mentoring-hub' && (
        <MentoringHubTab API_BASE={process.env.REACT_APP_API_BASE || ''} token={localStorage.getItem('authToken') || localStorage.getItem('token')} user={user} />
      )}

      {/* === CURRICULUM PLANNER TAB === */}
      {activeTab === 'curriculum-planner' && (
        <CurriculumPlannerTab API_BASE={process.env.REACT_APP_API_BASE || ''} token={localStorage.getItem('authToken') || localStorage.getItem('token')} />
      )}

      {/* Course Creation Modal */}
      {showCourseModal && (
        <div className="modal-overlay" onClick={() => {
          setShowCourseModal(false);
          // Reset form when closing
          setCourseFormData({
            title: '',
            description: '',
            category: '',
            level: 'Beginner',
            price: 0,
            duration: '',
            thumbnail: null
          });
        }}>
          <div className="course-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header with Gradient */}
            <div className="course-modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                </div>
                <div>
                  <h3>{courseFormData.courseId ? 'Edit Course' : 'Create New Course'}</h3>
                  <p>Design an engaging learning experience for your students</p>
                </div>
              </div>
              <button 
                className="close-button-modern"
                onClick={() => {
                  setShowCourseModal(false);
                  // Reset form when closing
                  setCourseFormData({
                    title: '',
                    description: '',
                    category: '',
                    level: 'Beginner',
                    price: 0,
                    duration: '',
                    thumbnail: null
                  });
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <form onSubmit={handleCreateCourse}>
              <div className="course-modal-body">
                {/* Course Title */}
                <div className="form-section">
                  <label className="form-label-modern">
                    <span className="label-icon">📚</span>
                    Course Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={courseFormData.title}
                    onChange={(e) => setCourseFormData({...courseFormData, title: e.target.value})}
                    className="form-input-modern"
                    placeholder="e.g., Introduction to Sustainable Energy"
                    required
                  />
                  <span className="input-hint">Make it clear, concise, and compelling</span>
                </div>

                {/* Description */}
                <div className="form-section">
                  <label className="form-label-modern">
                    <span className="label-icon">📝</span>
                    Course Description *
                  </label>
                  <textarea
                    name="description"
                    value={courseFormData.description}
                    onChange={(e) => setCourseFormData({...courseFormData, description: e.target.value})}
                    className="form-input-modern form-textarea"
                    rows="4"
                    placeholder="Describe what students will learn and why this course matters..."
                    required
                  ></textarea>
                  <span className="input-hint">Highlight key benefits and outcomes</span>
                </div>

                {/* Category and Level Grid */}
                <div className="form-grid-2">
                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">🏷️</span>
                      Category *
                    </label>
                    <select
                      name="category"
                      value={courseFormData.category}
                      onChange={(e) => setCourseFormData({...courseFormData, category: e.target.value})}
                      className="form-input-modern form-select"
                      required
                    >
                      <option value="">Select a category</option>
                      <option value="ai-sustainability">🤖 AI & Sustainability</option>
                      <option value="green-manufacturing">🏭 Green Manufacturing</option>
                      <option value="renewable-energy">⚡ Renewable Energy</option>
                      <option value="climate-tech">🌍 Climate Tech</option>
                      <option value="circular-economy">♻️ Circular Economy</option>
                      <option value="sustainable-design">🎨 Sustainable Design</option>
                      <option value="carbon-management">🌱 Carbon Management</option>
                      <option value="smart-cities">🏙️ Smart Cities</option>
                      <option value="environmental-engineering">🔧 Environmental Engineering</option>
                      <option value="clean-tech">✨ Clean Tech</option>
                      <option value="sustainability-leadership">👔 Sustainability Leadership</option>
                      <option value="data-science">📊 Data Science</option>
                    </select>
                  </div>

                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">📊</span>
                      Difficulty Level *
                    </label>
                    <select
                      name="level"
                      value={courseFormData.level}
                      onChange={(e) => setCourseFormData({...courseFormData, level: e.target.value})}
                      className="form-input-modern form-select"
                      required
                    >
                      <option value="">Choose level</option>
                      <option value="Beginner">🌱 Beginner - No prior knowledge needed</option>
                      <option value="Intermediate">📈 Intermediate - Some experience required</option>
                      <option value="Advanced">🚀 Advanced - Strong background needed</option>
                      <option value="Expert">💎 Expert - Professional level</option>
                    </select>
                  </div>
                </div>

                {/* Duration and Price Grid */}
                <div className="form-grid-2">
                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">⏱️</span>
                      Duration (hours) *
                    </label>
                    <input
                      type="number"
                      name="duration"
                      value={courseFormData.duration}
                      onChange={(e) => setCourseFormData({...courseFormData, duration: e.target.value})}
                      className="form-input-modern"
                      placeholder="e.g., 10"
                      min="0.5"
                      step="0.5"
                      required
                    />
                    <span className="input-hint">Total course length</span>
                  </div>

                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">💰</span>
                      Price (USD)
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={courseFormData.price}
                      onChange={(e) => setCourseFormData({...courseFormData, price: e.target.value})}
                      className="form-input-modern"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <span className="input-hint">Leave 0 for free course</span>
                  </div>
                </div>

                {/* Display Page Selection */}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="displayPage" className="form-label">
                      Display Page <span style={{color: '#7c3aed'}}>*</span>
                    </label>
                    <select
                      id="displayPage"
                      value={courseFormData.displayPage || 'courses'}
                      onChange={(e) => setCourseFormData({...courseFormData, displayPage: e.target.value})}
                      className="form-input-modern"
                      required
                    >
                      <option value="courses">Regular Courses Page Only</option>
                      <option value="strategy-planning">Strategy Planning Page Only</option>
                      <option value="both">Both Pages</option>
                    </select>
                    <span className="input-hint">Choose where this course will be displayed</span>
                  </div>
                </div>

                {/* Info Box */}
                <div className="info-box">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <div>
                    <strong>Next Steps:</strong> After creating your course, you'll be able to add lessons, upload materials, and customize the learning experience.
                  </div>
                </div>
              </div>

              <div className="course-modal-footer">
                <button
                  type="button"
                  className="btn-modern btn-secondary"
                  onClick={() => {
                    setShowCourseModal(false);
                    // Reset form when closing
                    setCourseFormData({
                      title: '',
                      description: '',
                      category: '',
                      level: 'Beginner',
                      price: 0,
                      duration: '',
                      thumbnail: null,
                      displayPage: 'courses'
                    });
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                  Cancel
                </button>
                <button type="submit" className="btn-modern btn-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                  {courseFormData.courseId ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Content Upload Modal */}
      {showContentModal && (
        <div className="modal-overlay" onClick={() => setShowContentModal(false)}>
          <div className="modal-content content-upload-modal" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleContentUpload}>
              {/* Modal Header */}
              <div className="content-modal-header">
                <div className="modal-header-content">
                  <div className="modal-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="17 8 12 3 7 8"/>
                      <line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                  </div>
                  <div>
                    <h2>Upload Content 📤</h2>
                    <p className="modal-subtitle">Add videos, documents, or resources to your courses</p>
                  </div>
                </div>
                <button
                  type="button"
                  className="close-button-modern"
                  onClick={() => setShowContentModal(false)}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="content-modal-body">
                {/* Content Name */}
                <div className="form-section">
                  <label className="form-label-modern">
                    <span className="label-icon">📝</span>
                    Content Name *
                  </label>
                  <input
                    type="text"
                    name="contentName"
                    className="form-input-modern"
                    placeholder="e.g., Lesson 1: Introduction to Solar Energy"
                    required
                  />
                  <span className="input-hint">Give your content a descriptive name</span>
                </div>

                {/* Content Type and Course Grid */}
                <div className="form-grid-2">
                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">🏷️</span>
                      Content Type *
                    </label>
                    <select
                      name="contentType"
                      className="form-select"
                      required
                    >
                      <option value="">Select type</option>
                      <option value="video">📹 Video</option>
                      <option value="pdf">📄 PDF Document</option>
                      <option value="zip">📦 Archive/Resources</option>
                      <option value="audio">🎵 Audio</option>
                      <option value="image">🖼️ Image</option>
                    </select>
                  </div>

                  <div className="form-section">
                    <label className="form-label-modern">
                      <span className="label-icon">📚</span>
                      Associated Course *
                    </label>
                    <select
                      name="course"
                      className="form-select"
                      required
                    >
                      <option value="">Select course</option>
                      <option value="AI for Sustainable Manufacturing">AI for Sustainable Manufacturing</option>
                      <option value="Renewable Energy Systems">Renewable Energy Systems</option>
                      <option value="Circular Economy Principles">Circular Economy Principles</option>
                      <option value="Climate Tech Innovation">Climate Tech Innovation</option>
                    </select>
                  </div>
                </div>

                {/* File Upload */}
                <div className="form-section">
                  <label className="form-label-modern">
                    <span className="label-icon">📎</span>
                    Upload File *
                  </label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      name="file"
                      id="content-file"
                      className="file-input-hidden"
                      accept="video/*,audio/*,image/*,.pdf,.zip,.rar"
                      required
                    />
                    <label htmlFor="content-file" className="file-upload-label">
                      <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <span className="upload-text">Click to browse or drag and drop</span>
                      <span className="upload-hint">Max file size: 500 MB</span>
                    </label>
                  </div>
                </div>

                {/* Description */}
                <div className="form-section">
                  <label className="form-label-modern">
                    <span className="label-icon">💬</span>
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    className="form-textarea"
                    rows="3"
                    placeholder="Brief description of this content..."
                  ></textarea>
                </div>

                {/* Info Box */}
                <div className="info-box">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="16" x2="12" y2="12"/>
                    <line x1="12" y1="8" x2="12.01" y2="8"/>
                  </svg>
                  <div>
                    <strong>Supported formats:</strong> Videos (MP4, MOV), Documents (PDF), Archives (ZIP, RAR), Audio (MP3, WAV), Images (JPG, PNG)
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="content-modal-footer">
                <button
                  type="button"
                  className="btn-modern btn-secondary"
                  onClick={() => setShowContentModal(false)}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"/>
                    <line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                  Cancel
                </button>
                <button type="submit" className="btn-modern btn-primary">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  Upload Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </AdminDashboardShell>
  );
};

export default ContentCreatorDashboard;
                   
